import { ImportLogsDialog } from '@/components/common/import-logs-dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { zyAuthor } from '@/lib/servers';
import { getCleanUuid, processTitle } from '@/lib/utils';
import { unwrapTopLevelZyVersionNodes } from '@/lib/zy-tiptap-utils';
import {
    createDevZybook,
    createMaDocumentImportLogs,
    deleteMaDocuments,
    getConvertedGdocJson,
    getMaDocumentsByZybook,
    getSameContentSourceTestQuestions,
    getSection,
    getSubject,
    getSubjectDevZybooks,
    getZybook,
    getZybookAboutPages,
    getZybookSubject,
    importMaDocuments,
    type GetConvertedGdocJsonOptions,
    type GetMaDocumentsResponse,
    type MaDocumentImportLog,
    type MaDocumentImportPayload,
    type ZyServerBook,
    type ZyServerSameContentSourceTestQuestion,
    type ZyServerSection,
    type ZyServerSubject,
    type ZyServerTestQuestion,
} from '@/lib/zybooks-api';
import {
    buildZyVersionIdMap,
    convertParseResponseSection,
    convertZybookTestQuestionsForImport,
    getZyVersionsFromRawZyXml,
    prepareSectionForImportConversion,
} from '@/lib/zybooks-parser';
import type { JSONContent } from '@tiptap/core';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, FileCheck, Loader2 } from 'lucide-react';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

type RowStatus = 'idle' | 'pending' | 'importing' | 'success' | 'failed';

type RowState = {
    status: RowStatus;
    error: string | null;
};

type GdocConvertedSection = {
    section: any | null;
    output: JSONContent | null;
    error: string | null;
};

type ImportCatalogTableRowProps = {
    row: SectionCatalogRow;
    isSelected: boolean;
    maDocumentName: string | null;
    maDocumentId: number | null;
    rowState: RowState;
    checkboxDisabled: boolean;
    onToggleGuid: (guid: string) => void;
    variant?: 'catalog' | 'unused';
    documentZybookCode?: string;
};

export type SectionCatalogRow = {
    zybook_code: string;
    section_guid: string;
    chapter_number: number;
    section_number: number;
    title: string;
    gdoc_id: string;
    gdoc_url: string;
    hasLabs: boolean;
    isUnused?: boolean;
    isAboutPage?: boolean;
    aboutChapterTitle?: string;
    canonical_section_id?: number;
    sourceChapterNumber?: number;
    sourceSectionNumber?: number;
    unusedChapterTitle?: string;
};

type TestQuestionCatalogTableRowProps = {
    row: TestQuestionCatalogRow;
    importState?: RowState;
};

export type CanonicalSectionRef = {
    zybook_code: string;
    chapter_number: number;
    section_number: number;
    section_title: string;
};

export type ImportSectionRef = CanonicalSectionRef & {
    section_guid: string;
};

export type TestQuestionCatalogRow = {
    rowKey: string;
    test_question_id: number;
    number: number;
    guid: string;
    canonical_section_id: number;
    release_id: number;
    matched_zybook_code: string | null;
    matched_chapter_number: number | null;
    matched_section_number: number | null;
    matched_section_title: string | null;
    matched_section_guid: string | null;
    question: ZyServerTestQuestion;
};

export type TestQuestionImportGroup = {
    groupKey: string;
    chapter_number: number;
    section_number: number;
    section_title: string;
    section_guid: string;
    questions: ZyServerTestQuestion[];
};

export type TestQuestionSectionCatalogGroup = {
    sectionKey: string;
    matched_chapter_number: number;
    matched_section_number: number;
    matched_section_title: string | null;
    matched_section_guid: string | null;
    rows: TestQuestionCatalogRow[];
};

export type BuildTestQuestionImportPayloadContext = {
    importZybookCode: string;
    contentSource: string;
    sectionMaDocumentId: number;
    chapterTitle: string;
    existingTestQuestionDocumentName?: string | null;
    sameContentSourceSubjectCount: number;
    releaseId: number | null;
};

export function testQuestionSectionKey(chapterNumber: number | null, sectionNumber: number | null): string | null {
    if (chapterNumber == null || sectionNumber == null) {
        return null;
    }
    return maSectionKey(chapterNumber, sectionNumber);
}

export function testQuestionRowKey(canonicalSectionId: number, testQuestionId: number): string {
    return `${canonicalSectionId}:${testQuestionId}`;
}

export function contentCoords(row: SectionCatalogRow): {
    zybookCode: string;
    chapterNumber: number;
    sectionNumber: number;
} {
    if (row.isUnused) {
        return {
            zybookCode: row.zybook_code,
            chapterNumber: row.sourceChapterNumber!,
            sectionNumber: row.sourceSectionNumber!,
        };
    }
    return {
        zybookCode: row.zybook_code,
        chapterNumber: row.chapter_number,
        sectionNumber: row.section_number,
    };
}

export function getTestQuestionSectionImportState(sectionKey: string, states: Record<string, RowState>): RowState | undefined {
    return states[sectionKey];
}

export function maSectionKey(chapterNumber: number, sectionNumber: number): string {
    return `${chapterNumber}:${sectionNumber}`;
}

export function parseReleaseId(input: string): number | null {
    const trimmed = input.trim();
    if (!trimmed) {
        return null;
    }
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) ? parsed : null;
}

export function resolveImportImageReleaseId(releaseIdInput: string, devbookReleaseNumber: number): string {
    const releaseId = parseReleaseId(releaseIdInput);
    return releaseId != null ? String(releaseId) : String(devbookReleaseNumber);
}

export function getRowMaDocumentName(
    row: SectionCatalogRow,
    namesBySection: Map<string, string>,
    namesByGuid: Map<string, string>,
): string | undefined {
    if (row.isUnused) {
        return namesByGuid.get(row.section_guid);
    }
    return namesBySection.get(maSectionKey(row.chapter_number, row.section_number));
}

export function getRowMaDocumentId(
    row: SectionCatalogRow,
    idsBySection: Map<string, number>,
    idsByGuid: Map<string, number>,
): number | undefined {
    if (row.isUnused) {
        return idsByGuid.get(row.section_guid);
    }
    return idsBySection.get(maSectionKey(row.chapter_number, row.section_number));
}

export function rowHasMaDocument(row: SectionCatalogRow, namesBySection: Map<string, string>, namesByGuid: Map<string, string>): boolean {
    return Boolean(getRowMaDocumentName(row, namesBySection, namesByGuid)?.trim());
}

export function buildDocumentMapsBySection(rows: GetMaDocumentsResponse[]): {
    namesBySection: Map<string, string>;
    idsBySection: Map<string, number>;
    namesByGuid: Map<string, string>;
    idsByGuid: Map<string, number>;
    testQuestionNamesBySection: Map<string, string[]>;
    testQuestionNamesByGuid: Map<string, string[]>;
} {
    const namesBySection = new Map<string, string>();
    const idsBySection = new Map<string, number>();
    const namesByGuid = new Map<string, string>();
    const idsByGuid = new Map<string, number>();
    const testQuestionNamesBySection = new Map<string, string[]>();
    const testQuestionNamesByGuid = new Map<string, string[]>();

    const appendTestQuestionName = (map: Map<string, string[]>, key: string, name: string) => {
        const existing = map.get(key) ?? [];
        if (!existing.includes(name)) {
            map.set(key, [...existing, name]);
        }
    };

    for (const d of rows) {
        const ch = Number(d.chapter_number);
        const sec = Number(d.section_number);
        const linkedTestQuestionNames = (d.test_question_documents ?? [])
            .map(link => link.test_question_document_name?.trim())
            .filter((name): name is string => Boolean(name));

        if (Number.isFinite(ch) && Number.isFinite(sec)) {
            const key = maSectionKey(ch, sec);
            if (d.document_name?.trim()) {
                namesBySection.set(key, d.document_name);
            }
            if (d.document_id != null) {
                idsBySection.set(key, d.document_id);
            }
            for (const testQuestionName of linkedTestQuestionNames) {
                appendTestQuestionName(testQuestionNamesBySection, key, testQuestionName);
            }
        }
        const guid = d.section_guid ? getCleanUuid(d.section_guid) : '';
        if (guid) {
            if (d.document_name?.trim()) {
                namesByGuid.set(guid, d.document_name);
            }
            if (d.document_id != null) {
                idsByGuid.set(guid, d.document_id);
            }
            for (const testQuestionName of linkedTestQuestionNames) {
                appendTestQuestionName(testQuestionNamesByGuid, guid, testQuestionName);
            }
        }
    }
    return {
        namesBySection,
        idsBySection,
        namesByGuid,
        idsByGuid,
        testQuestionNamesBySection,
        testQuestionNamesByGuid,
    };
}

export function getTestQuestionDocumentName(
    chapterNumber: number,
    sectionNumber: number,
    sectionGuid: string,
    namesBySection: Map<string, string[]>,
    namesByGuid: Map<string, string[]>,
): string | undefined {
    const names = namesBySection.get(maSectionKey(chapterNumber, sectionNumber)) ?? namesByGuid.get(getCleanUuid(sectionGuid)) ?? [];
    return names[0];
}

/**
 * Whether a catalog section row corresponds to a parsed Google Doc section object.
 *
 * @param selectedSection - Row from the zybook catalog (`section_guid`, chapter/section indices).
 * @param convertedSection - Raw section object from gdoc / parser output (`id`, `chapter_number`, `section_number`).
 * @returns True if the catalog row and converted section describe the same logical section.
 */
function isSectionMatch(
    selectedSection: { section_guid: string; chapter_number: number; section_number: number },
    convertedSection: { id?: string; section_number?: number; chapter_number?: string | number } | null | undefined,
): boolean {
    const convertedSectionId = convertedSection?.id as string | undefined;
    if (convertedSectionId) {
        if (selectedSection.section_guid === convertedSectionId) {
            return true;
        }
        // Handle cases where guid is suffixed with version
        if (convertedSectionId.startsWith(selectedSection.section_guid) || selectedSection.section_guid.startsWith(convertedSectionId)) {
            return true;
        }
    }
    return false;
}

function removeGuidVersionSuffix(guid: string, subjectVersion: string | undefined): string {
    let cleanedGuid = guid;
    const trimmedVersion = subjectVersion?.trim();
    if (trimmedVersion && cleanedGuid.endsWith(trimmedVersion)) {
        cleanedGuid = cleanedGuid.slice(0, -trimmedVersion.length);
    }
    return cleanedGuid;
}

function stripSubjectVersionFromSectionIds(sections: Record<string, any>, subjectVersion: string | undefined): any[] {
    if (!Array.isArray(sections)) {
        return [];
    }
    return sections.map(section => {
        if (!section) {
            return section;
        }
        const s = section as Record<string, unknown>;
        const id = s.id;
        if (typeof id !== 'string') {
            return section;
        }
        return { ...s, id: removeGuidVersionSuffix(id, subjectVersion) };
    });
}

/**
 * Loads the section catalog used by the zybook import UI.
 *
 * @param zybook - zyBook metadata already loaded via {@link getZybook}.
 * @param zybookCode - Development / catalog zybook code.
 * @returns Flat list of {@link SectionCatalogRow} with Google Doc links built from each section’s `gdoc_id`.
 */
function mapSectionToCatalogRow(section: any, zybookCode: string, defaultChapterNumber?: number): SectionCatalogRow {
    const section_guid = getCleanUuid(section.guid);
    const gdocId = section.parent_gdoc_id ?? section.gdoc_id ?? '';
    return {
        zybook_code: zybookCode,
        section_guid,
        chapter_number: defaultChapterNumber ?? section.chapter_number ?? section.canonical_chapter_number ?? 0,
        section_number: section.number,
        title: section.title ?? '',
        gdoc_id: gdocId,
        gdoc_url: gdocId ? `https://docs.google.com/document/d/${gdocId}` : '',
        hasLabs: section.metadata?.hasLabs === true,
    };
}

export function extractZybookSections(zybook: ZyServerBook, zybookCode: string): SectionCatalogRow[] {
    function extractSections(sections: any[] | undefined, defaultChapterNumber?: number): SectionCatalogRow[] {
        if (!Array.isArray(sections)) return [];
        return sections.map(section => mapSectionToCatalogRow(section, zybookCode, defaultChapterNumber));
    }

    if (!Array.isArray(zybook.chapters)) {
        return [];
    }

    return zybook.chapters.flatMap((chapter: ZyServerBook['chapters'][number]) => extractSections(chapter.sections, chapter.number));
}

/**
 * All sections from a zybook outline, including the `unused` array.
 */
export function extractAllZybookSections(zybook: ZyServerBook, zybookCode: string): SectionCatalogRow[] {
    const fromChapters = extractZybookSections(zybook, zybookCode);
    const fromUnused = (zybook.unused ?? []).map(section =>
        mapSectionToCatalogRow(section, zybookCode, section.canonical_chapter_number ?? -1),
    );
    return [...fromChapters, ...fromUnused];
}

export function formatUnusedChapterTitle(zybookCode: string, sourceChapterTitle: string): string {
    return `[UNUSED] ${zybookCode} - ${sourceChapterTitle}`;
}

const ABOUT_PAGE_CHAPTER_NUMBER = 0;
const ABOUT_PAGE_CHAPTER_TITLE = 'About';

export function mapAboutPageToCatalogRow(page: ZyServerSection, zybookCode: string, sectionNumber: number): SectionCatalogRow {
    const section_guid = getCleanUuid(page.guid ?? '');
    const gdocId = page.parent_gdoc_id ?? page.gdoc_id ?? '';
    return {
        zybook_code: zybookCode,
        section_guid,
        chapter_number: ABOUT_PAGE_CHAPTER_NUMBER,
        section_number: sectionNumber,
        title: page.title ?? '',
        gdoc_id: gdocId,
        gdoc_url: gdocId ? `https://docs.google.com/document/d/${gdocId}` : '',
        hasLabs: false,
        isAboutPage: true,
        aboutChapterTitle: ABOUT_PAGE_CHAPTER_TITLE,
        canonical_section_id: page.canonical_section_id,
    };
}

export function computeAboutPageCatalog(aboutPages: ZyServerSection[], zybookCode: string): SectionCatalogRow[] {
    return aboutPages.map((page, index) => mapAboutPageToCatalogRow(page, zybookCode, index + 1));
}

function resolveSourceChapterTitle(book: ZyServerBook, chapterNumber: number): string {
    if (chapterNumber === -1) {
        return 'Unused';
    }
    return book.chapters?.find(c => c.number === chapterNumber)?.title ?? `Chapter ${chapterNumber}`;
}

function compareSourceChapterNumbers(a: number, b: number): number {
    if (a === -1) {
        return 1;
    }
    if (b === -1) {
        return -1;
    }
    return a - b;
}

/**
 * Sections present in other dev books but not in the catalog zybook (by GUID).
 * Assigns MA chapters after `baseChapterNumber`, grouped by source book + source chapter.
 */
export function computeUnusedSectionsFromOtherDevBooks(
    otherDevBooks: ZyServerBook[],
    catalogGuids: Set<string>,
    baseChapterNumber: number,
): SectionCatalogRow[] {
    const seenGuids = new Set<string>();
    const candidates: SectionCatalogRow[] = [];
    const booksByCode = new Map<string, ZyServerBook>();

    // Picks sections from other dev books that are not already imported or in the catalog book.
    const sortedBooks = [...otherDevBooks].sort((a, b) => a.zybook_code.localeCompare(b.zybook_code));
    for (const book of sortedBooks) {
        booksByCode.set(book.zybook_code, book);
        for (const row of extractAllZybookSections(book, book.zybook_code)) {
            if (!row.section_guid || catalogGuids.has(row.section_guid) || seenGuids.has(row.section_guid)) {
                continue;
            }
            seenGuids.add(row.section_guid);
            candidates.push(row);
        }
    }

    // Groups sections by source book and chapter.
    const groups = new Map<string, SectionCatalogRow[]>();
    for (const row of candidates) {
        const groupKey = `${row.zybook_code}:${row.chapter_number}`;
        const list = groups.get(groupKey) ?? [];
        list.push(row);
        groups.set(groupKey, list);
    }

    // Sorts groups by book code and chapter number.
    const sortedGroupKeys = [...groups.keys()].sort((a, b) => {
        const [bookA, chapterA] = a.split(':');
        const [bookB, chapterB] = b.split(':');
        const bookCompare = bookA.localeCompare(bookB);
        if (bookCompare !== 0) {
            return bookCompare;
        }
        return compareSourceChapterNumbers(Number(chapterA), Number(chapterB));
    });

    const result: SectionCatalogRow[] = [];
    let nextChapterNumber = baseChapterNumber + 1;
    for (const groupKey of sortedGroupKeys) {
        const rows = groups.get(groupKey)!;
        const [zybookCode, sourceChapterRaw] = groupKey.split(':');
        const sourceChapterNumber = Number(sourceChapterRaw);
        const book = booksByCode.get(zybookCode)!;
        const unusedChapterTitle = formatUnusedChapterTitle(zybookCode, resolveSourceChapterTitle(book, sourceChapterNumber));

        rows.sort((a, b) => a.section_number - b.section_number);
        for (const row of rows) {
            result.push({
                ...row,
                isUnused: true,
                sourceChapterNumber: row.chapter_number,
                sourceSectionNumber: row.section_number,
                chapter_number: nextChapterNumber,
                section_number: row.section_number,
                unusedChapterTitle,
            });
        }
        nextChapterNumber++;
    }

    return result;
}

export function buildCanonicalSectionIndex(books: ZyServerBook[]): Map<number, CanonicalSectionRef> {
    const index = new Map<number, CanonicalSectionRef>();
    for (const book of books) {
        for (const chapter of book.chapters ?? []) {
            for (const section of chapter.sections ?? []) {
                if (section.canonical_section_id != null && !index.has(section.canonical_section_id)) {
                    index.set(section.canonical_section_id, {
                        zybook_code: book.zybook_code,
                        chapter_number: chapter.number,
                        section_number: section.number,
                        section_title: section.title,
                    });
                }
            }
        }
        for (const section of book.unused ?? []) {
            if (section.canonical_section_id != null && !index.has(section.canonical_section_id)) {
                index.set(section.canonical_section_id, {
                    zybook_code: book.zybook_code,
                    chapter_number: section.canonical_chapter_number ?? -1,
                    section_number: section.number,
                    section_title: section.title,
                });
            }
        }
    }
    return index;
}

export function dedupeZyServerTestQuestionsByGuid(
    questions: ZyServerSameContentSourceTestQuestion[],
): ZyServerSameContentSourceTestQuestion[] {
    const bestByGuid = new Map<string, ZyServerSameContentSourceTestQuestion>();
    for (const question of questions) {
        const existing = bestByGuid.get(question.guid);
        if (!existing || question.release_id > existing.release_id) {
            bestByGuid.set(question.guid, question);
        }
    }
    return [...bestByGuid.values()];
}

export function dedupeTestQuestionCatalogRows(rows: TestQuestionCatalogRow[]): TestQuestionCatalogRow[] {
    const bestByGuid = new Map<string, TestQuestionCatalogRow>();
    for (const row of rows) {
        const existing = bestByGuid.get(row.guid);
        if (!existing || row.release_id > existing.release_id) {
            bestByGuid.set(row.guid, row);
        }
    }
    return [...bestByGuid.values()];
}

export function buildImportSectionIndexFromBooks(books: ZyServerBook[]): Map<number, ImportSectionRef> {
    const index = new Map<number, ImportSectionRef>();
    for (const book of books) {
        for (const chapter of book.chapters ?? []) {
            for (const section of chapter.sections ?? []) {
                if (section.canonical_section_id != null && !index.has(section.canonical_section_id)) {
                    index.set(section.canonical_section_id, {
                        zybook_code: book.zybook_code,
                        chapter_number: chapter.number,
                        section_number: section.number,
                        section_title: section.title,
                        section_guid: getCleanUuid(section.guid),
                    });
                }
            }
        }
        for (const section of book.unused ?? []) {
            if (section.canonical_section_id != null && !index.has(section.canonical_section_id)) {
                index.set(section.canonical_section_id, {
                    zybook_code: book.zybook_code,
                    chapter_number: section.canonical_chapter_number ?? -1,
                    section_number: section.number,
                    section_title: section.title,
                    section_guid: getCleanUuid(section.guid),
                });
            }
        }
    }
    return index;
}

export function buildImportSectionIndex(book: ZyServerBook): Map<number, ImportSectionRef> {
    return buildImportSectionIndexFromBooks([book]);
}

export function buildCatalogSectionCoordsIndex(book: ZyServerBook): Map<string, ImportSectionRef> {
    const index = new Map<string, ImportSectionRef>();
    for (const chapter of book.chapters ?? []) {
        for (const section of chapter.sections ?? []) {
            const key = maSectionKey(chapter.number, section.number);
            if (!index.has(key)) {
                index.set(key, {
                    zybook_code: book.zybook_code,
                    chapter_number: chapter.number,
                    section_number: section.number,
                    section_title: section.title,
                    section_guid: getCleanUuid(section.guid),
                });
            }
        }
    }
    for (const section of book.unused ?? []) {
        const chapterNumber = section.canonical_chapter_number ?? -1;
        const key = maSectionKey(chapterNumber, section.number);
        if (!index.has(key)) {
            index.set(key, {
                zybook_code: book.zybook_code,
                chapter_number: chapterNumber,
                section_number: section.number,
                section_title: section.title,
                section_guid: getCleanUuid(section.guid),
            });
        }
    }
    return index;
}

export function buildTestQuestionCatalogRows(
    questions: ZyServerSameContentSourceTestQuestion[],
    catalogSectionCoordsIndex: Map<string, ImportSectionRef>,
): TestQuestionCatalogRow[] {
    return questions.map(question => {
        const sectionKey = maSectionKey(question.chapter_number, question.section_number);
        const catalogMatch = catalogSectionCoordsIndex.get(sectionKey);
        return {
            rowKey: testQuestionRowKey(question.canonical_section_id, question.test_question_id),
            test_question_id: question.test_question_id,
            number: question.number,
            guid: question.guid,
            canonical_section_id: question.canonical_section_id,
            release_id: question.release_id,
            matched_chapter_number: question.chapter_number,
            matched_section_number: question.section_number,
            matched_section_title: catalogMatch?.section_title ?? null,
            matched_section_guid: catalogMatch?.section_guid ?? null,
            matched_zybook_code: catalogMatch?.zybook_code ?? null,
            question,
        };
    });
}

export function groupTestQuestionsByImportSection(rows: TestQuestionCatalogRow[]): TestQuestionImportGroup[] {
    const groups = new Map<string, TestQuestionImportGroup>();
    for (const row of rows) {
        if (row.matched_chapter_number == null || row.matched_section_number == null || !row.matched_section_guid) {
            continue;
        }
        const groupKey = maSectionKey(row.matched_chapter_number, row.matched_section_number);
        const existing = groups.get(groupKey);
        if (existing) {
            if (!existing.questions.some(q => q.test_question_id === row.question.test_question_id)) {
                existing.questions.push(row.question);
            }
            continue;
        }
        groups.set(groupKey, {
            groupKey,
            chapter_number: row.matched_chapter_number,
            section_number: row.matched_section_number,
            section_title: row.matched_section_title ?? '',
            section_guid: row.matched_section_guid,
            questions: [row.question],
        });
    }
    for (const group of groups.values()) {
        group.questions.sort((a, b) => a.number - b.number);
    }
    return [...groups.values()].sort((a, b) => {
        if (a.chapter_number !== b.chapter_number) {
            return a.chapter_number - b.chapter_number;
        }
        return a.section_number - b.section_number;
    });
}

export function buildTestQuestionSectionCatalogGroups(rows: TestQuestionCatalogRow[]): TestQuestionSectionCatalogGroup[] {
    const groups = new Map<string, TestQuestionCatalogRow[]>();
    for (const row of rows) {
        const sectionKey = testQuestionSectionKey(row.matched_chapter_number, row.matched_section_number);
        if (sectionKey == null) {
            continue;
        }
        const list = groups.get(sectionKey) ?? [];
        list.push(row);
        groups.set(sectionKey, list);
    }

    return [...groups.entries()]
        .map(([sectionKey, sectionRows]) => {
            const sortedRows = [...sectionRows].sort((a, b) => a.number - b.number);
            const first = sortedRows[0];
            return {
                sectionKey,
                matched_chapter_number: first.matched_chapter_number!,
                matched_section_number: first.matched_section_number!,
                matched_section_title: first.matched_section_title,
                matched_section_guid: first.matched_section_guid,
                rows: sortedRows,
            };
        })
        .sort((a, b) => {
            if (a.matched_chapter_number !== b.matched_chapter_number) {
                return a.matched_chapter_number - b.matched_chapter_number;
            }
            return a.matched_section_number - b.matched_section_number;
        });
}

export function buildTestQuestionImportPayload(
    group: TestQuestionImportGroup,
    ctx: BuildTestQuestionImportPayloadContext,
): MaDocumentImportPayload {
    const documentName = ctx.existingTestQuestionDocumentName?.trim()
        ? ctx.existingTestQuestionDocumentName
        : `${ctx.contentSource}/test-questions/${uuidv4()}`;

    return {
        document_name: documentName,
        chapter_number: group.chapter_number,
        section_number: group.section_number,
        chapter_title: ctx.chapterTitle,
        short_chapter_title: null,
        section_title: group.section_title,
        short_section_title: null,
        section_guid: group.section_guid,
        zybook_code: ctx.importZybookCode,
        tiptap_json: convertZybookTestQuestionsForImport(group.questions, {
            sameContentSourceSubjectCount: ctx.sameContentSourceSubjectCount,
        }),
        release_id: ctx.releaseId,
        status: ctx.releaseId != null ? 'released' : 'dev',
        type: 'test_question',
        link_section_ma_document_id: ctx.sectionMaDocumentId,
    };
}

export function sortTestQuestionCatalogRows(rows: TestQuestionCatalogRow[]): TestQuestionCatalogRow[] {
    return [...rows].sort((a, b) => {
        const chapterA = a.matched_chapter_number ?? Number.MAX_SAFE_INTEGER;
        const chapterB = b.matched_chapter_number ?? Number.MAX_SAFE_INTEGER;
        if (chapterA !== chapterB) {
            return chapterA - chapterB;
        }
        const sectionA = a.matched_section_number ?? Number.MAX_SAFE_INTEGER;
        const sectionB = b.matched_section_number ?? Number.MAX_SAFE_INTEGER;
        if (sectionA !== sectionB) {
            return sectionA - sectionB;
        }
        if (a.canonical_section_id !== b.canonical_section_id) {
            return a.canonical_section_id - b.canonical_section_id;
        }
        return a.number - b.number;
    });
}

const StatusCell = memo(function StatusCell({ state }: { state: RowState }) {
    if (state.status === 'pending') {
        return <span className="text-muted-foreground text-sm">Pending</span>;
    }
    if (state.status === 'importing') {
        return (
            <span className="text-muted-foreground flex items-center gap-1 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Importing
            </span>
        );
    }
    if (state.status === 'success') {
        return (
            <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-500">
                <CheckCircle2 className="h-4 w-4" />
                Success
            </span>
        );
    }
    if (state.status === 'failed') {
        return (
            <span
                className="flex flex-col gap-0.5 text-sm text-destructive"
                title={state.error ?? ''}
            >
                <span className="flex items-center gap-1">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    Failed
                </span>
                {state.error && <span className="text-muted-foreground max-w-[220px] text-xs">{state.error}</span>}
            </span>
        );
    }
    return <span className="text-muted-foreground text-sm">—</span>;
});

const ImportCatalogTableRow = memo(function ImportCatalogTableRow({
    row,
    isSelected,
    maDocumentName,
    maDocumentId,
    rowState,
    checkboxDisabled,
    onToggleGuid,
    variant = 'catalog',
    documentZybookCode,
}: ImportCatalogTableRowProps) {
    const isUnusedVariant = variant === 'unused';
    const maZybookCode = documentZybookCode ?? row.zybook_code;
    const onCheckedChange = useCallback(() => {
        onToggleGuid(row.section_guid);
    }, [onToggleGuid, row.section_guid]);

    const handleRowClick = useCallback(() => {
        if (!checkboxDisabled) {
            onToggleGuid(row.section_guid);
        }
    }, [checkboxDisabled, onToggleGuid, row.section_guid]);

    return (
        <TableRow
            className={
                checkboxDisabled
                    ? isUnusedVariant
                        ? 'bg-muted/40'
                        : undefined
                    : isUnusedVariant
                      ? 'bg-muted/40 cursor-pointer'
                      : 'cursor-pointer'
            }
            onClick={handleRowClick}
        >
            <TableCell onClick={e => e.stopPropagation()}>
                <Checkbox
                    checked={isSelected}
                    onCheckedChange={onCheckedChange}
                    disabled={checkboxDisabled}
                    aria-label={`Select ${row.title || 'section'} (chapter ${row.chapter_number}, section ${row.section_number})`}
                />
            </TableCell>
            {isUnusedVariant && (
                <TableCell
                    className="max-w-[140px] truncate font-mono text-xs"
                    title={row.zybook_code}
                >
                    {row.zybook_code}
                </TableCell>
            )}
            {isUnusedVariant ? (
                <>
                    <TableCell className="text-right tabular-nums">{row.sourceChapterNumber}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.sourceSectionNumber}</TableCell>
                </>
            ) : (
                <>
                    <TableCell className="text-right tabular-nums">{row.chapter_number}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.section_number}</TableCell>
                </>
            )}
            <TableCell>
                <span className="font-medium">{row.title || '—'}</span>
                {isUnusedVariant && (
                    <span className="text-muted-foreground ml-2 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                        Unused
                    </span>
                )}
            </TableCell>
            <TableCell
                className="font-mono text-xs"
                title={row.section_guid}
            >
                {row.section_guid}
            </TableCell>
            <TableCell className="text-center">
                {maDocumentName || maDocumentId != null ? (
                    maDocumentId != null ? (
                        <Link
                            to={`/documents/${maDocumentId}?zybookCode=${encodeURIComponent(maZybookCode)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-1 rounded-md border border-emerald-600 bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-950 transition-colors hover:bg-emerald-200/80 dark:border-emerald-500 dark:bg-emerald-950/80 dark:text-emerald-50 dark:hover:bg-emerald-900/90"
                            title="Open modern authoring document for this chapter and section."
                            onClick={e => e.stopPropagation()}
                        >
                            <FileCheck
                                className="h-3.5 w-3.5 shrink-0"
                                aria-hidden
                            />
                            Yes
                        </Link>
                    ) : (
                        <span
                            className="inline-flex items-center justify-center gap-1 rounded-md border border-emerald-600 bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-950 dark:border-emerald-500 dark:bg-emerald-950/80 dark:text-emerald-50"
                            title="MA document present; document id not loaded yet. Refresh the catalog."
                        >
                            <FileCheck
                                className="h-3.5 w-3.5 shrink-0"
                                aria-hidden
                            />
                            Yes
                        </span>
                    )
                ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                )}
            </TableCell>
            <TableCell className="hidden max-w-[200px] truncate font-mono text-xs md:table-cell">
                <Link
                    className="text-primary font-medium underline"
                    to={row.gdoc_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                >
                    {row.gdoc_id}
                </Link>
            </TableCell>
            <TableCell>
                <StatusCell state={rowState} />
            </TableCell>
        </TableRow>
    );
});

const TestQuestionCatalogTableRow = memo(function TestQuestionCatalogTableRow({ row, importState }: TestQuestionCatalogTableRowProps) {
    return (
        <TableRow>
            <TableCell className="text-right tabular-nums">{row.number}</TableCell>
            <TableCell className="text-right tabular-nums">{row.test_question_id}</TableCell>
            <TableCell className="text-right tabular-nums">{row.question.subject_id}</TableCell>
            <TableCell className="text-right tabular-nums">{row.release_id}</TableCell>
            <TableCell
                className="font-mono text-xs"
                title={row.guid}
            >
                {row.guid}
            </TableCell>
            <TableCell>
                <StatusCell state={importState ?? { status: 'idle', error: null }} />
            </TableCell>
        </TableRow>
    );
});

export default function ZybookImport() {
    const [zybookCodeInput, setZybookCodeInput] = useState('');
    const [overrideCatalogZybookCode, setOverrideCatalogZybookCode] = useState('');
    const [overrideSearchSnippetZybookCode, setOverrideSearchSnippetZybookCode] = useState('');
    const [overrideAboutPagesZybookCode, setOverrideAboutPagesZybookCode] = useState('');
    const [activeZybookCode, setActiveZybookCode] = useState<string | null>(null);
    const [activeZybookId, setActiveZybookId] = useState<number | null>(null);
    const [activeSubject, setActiveSubject] = useState<ZyServerSubject | null>(null);
    const [catalog, setCatalog] = useState<SectionCatalogRow[]>([]);
    const [unusedCatalog, setUnusedCatalog] = useState<SectionCatalogRow[]>([]);
    const [unusedFilter, setUnusedFilter] = useState('');
    const [catalogPanelOpen, setCatalogPanelOpen] = useState(true);
    const [unusedPanelOpen, setUnusedPanelOpen] = useState(false);
    const [lastLoadedWithUnusedEnabled, setLastLoadedWithUnusedEnabled] = useState<boolean | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
    const [importRunning, setImportRunning] = useState(false);
    const [deleteRunning, setDeleteRunning] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [shouldProcessWithVersion, setShouldProcessWithVersion] = useState(false);
    const [overrideContentVersion, setOverrideContentVersion] = useState('');
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importLogsDialogOpen, setImportLogsDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [documentNamesBySectionKey, setDocumentNamesBySectionKey] = useState<Map<string, string>>(() => new Map());
    const [documentIdsBySectionKey, setDocumentIdsBySectionKey] = useState<Map<string, number>>(() => new Map());
    const [documentNamesByGuid, setDocumentNamesByGuid] = useState<Map<string, string>>(() => new Map());
    const [documentIdsByGuid, setDocumentIdsByGuid] = useState<Map<string, number>>(() => new Map());
    const [testQuestionNamesBySectionKey, setTestQuestionNamesBySectionKey] = useState<Map<string, string[]>>(() => new Map());
    const [testQuestionNamesByGuid, setTestQuestionNamesByGuid] = useState<Map<string, string[]>>(() => new Map());
    const [chapterSelectKey, setChapterSelectKey] = useState(0);
    const [showOnlyWithoutMaDoc, setShowOnlyWithoutMaDoc] = useState(false);
    const [importUnusedFromAllDevBooks, setImportUnusedFromAllDevBooks] = useState(true);
    const [includeTestQuestions, setIncludeTestQuestions] = useState(true);
    const [includeAboutPages, setIncludeAboutPages] = useState(true);
    const [skipShareExisting, setSkipShareExisting] = useState(false);
    const [releaseIdInput, setReleaseIdInput] = useState('');
    const [aboutPageCatalog, setAboutPageCatalog] = useState<SectionCatalogRow[]>([]);
    const [aboutFilter, setAboutFilter] = useState('');
    const [aboutPanelOpen, setAboutPanelOpen] = useState(false);
    const [lastLoadedWithAboutPagesEnabled, setLastLoadedWithAboutPagesEnabled] = useState<boolean | null>(null);
    const [testQuestionCatalog, setTestQuestionCatalog] = useState<TestQuestionCatalogRow[]>([]);
    const [allTestQuestionCatalogRows, setAllTestQuestionCatalogRows] = useState<TestQuestionCatalogRow[]>([]);
    const [sameContentSourceSubjectCount, setSameContentSourceSubjectCount] = useState<number | null>(null);
    const [testQuestionFilter, setTestQuestionFilter] = useState('');
    const [testQuestionsPanelOpen, setTestQuestionsPanelOpen] = useState(false);
    const [lastLoadedWithTestQuestionsEnabled, setLastLoadedWithTestQuestionsEnabled] = useState<boolean | null>(null);
    const [selectedTestQuestionSectionKeys, setSelectedTestQuestionSectionKeys] = useState<Set<string>>(() => new Set());
    const [testQuestionImportStates, setTestQuestionImportStates] = useState<Record<string, RowState>>({});
    const [activeZybook, setActiveZybook] = useState<ZyServerBook | null>(null);
    const [devZybookSubjectIdInput, setDevZybookSubjectIdInput] = useState('');
    const [devZybookNewCodeInput, setDevZybookNewCodeInput] = useState('');
    const [devZybookTitleInput, setDevZybookTitleInput] = useState('');
    const [createDevZybookRunning, setCreateDevZybookRunning] = useState(false);
    const [createDevZybookError, setCreateDevZybookError] = useState<string | null>(null);
    const [createDevZybookSuccess, setCreateDevZybookSuccess] = useState<string | null>(null);
    const [createDevZybookOpen, setCreateDevZybookOpen] = useState(false);

    const allCatalogRows = useMemo(() => [...catalog, ...unusedCatalog, ...aboutPageCatalog], [catalog, unusedCatalog, aboutPageCatalog]);

    const visibleAboutCatalog = useMemo(() => {
        const filter = aboutFilter.trim().toLowerCase();
        return aboutPageCatalog.filter(row => {
            if (showOnlyWithoutMaDoc && rowHasMaDocument(row, documentNamesBySectionKey, documentNamesByGuid)) {
                return false;
            }
            if (!filter) {
                return true;
            }
            return (
                row.title.toLowerCase().includes(filter) ||
                row.section_guid.toLowerCase().includes(filter) ||
                String(row.canonical_section_id ?? '').includes(filter)
            );
        });
    }, [aboutPageCatalog, aboutFilter, showOnlyWithoutMaDoc, documentNamesBySectionKey, documentNamesByGuid]);

    const visibleCatalog = useMemo(() => {
        if (!showOnlyWithoutMaDoc) {
            return catalog;
        }
        return catalog.filter(r => !rowHasMaDocument(r, documentNamesBySectionKey, documentNamesByGuid));
    }, [catalog, documentNamesBySectionKey, documentNamesByGuid, showOnlyWithoutMaDoc]);

    const visibleUnusedCatalog = useMemo(() => {
        const filter = unusedFilter.trim().toLowerCase();
        return unusedCatalog.filter(row => {
            if (showOnlyWithoutMaDoc && rowHasMaDocument(row, documentNamesBySectionKey, documentNamesByGuid)) {
                return false;
            }
            if (!filter) {
                return true;
            }
            return (
                row.title.toLowerCase().includes(filter) ||
                row.section_guid.toLowerCase().includes(filter) ||
                row.zybook_code.toLowerCase().includes(filter)
            );
        });
    }, [unusedCatalog, unusedFilter, showOnlyWithoutMaDoc, documentNamesBySectionKey, documentNamesByGuid]);

    const unusedSectionsBySource = useMemo(() => {
        const groups = new Map<string, SectionCatalogRow[]>();
        for (const row of visibleUnusedCatalog) {
            const list = groups.get(row.zybook_code) ?? [];
            list.push(row);
            groups.set(row.zybook_code, list);
        }
        return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
    }, [visibleUnusedCatalog]);

    const visibleTestQuestionCatalog = useMemo(() => {
        const filter = testQuestionFilter.trim().toLowerCase();
        if (!filter) {
            return testQuestionCatalog;
        }
        return testQuestionCatalog.filter(row => {
            return (
                String(row.canonical_section_id).includes(filter) ||
                row.guid.toLowerCase().includes(filter) ||
                String(row.test_question_id).includes(filter) ||
                String(row.question.subject_id).includes(filter) ||
                String(row.release_id).includes(filter) ||
                (row.matched_section_title?.toLowerCase().includes(filter) ?? false) ||
                (row.matched_chapter_number != null && String(row.matched_chapter_number).includes(filter)) ||
                (row.matched_section_number != null && String(row.matched_section_number).includes(filter))
            );
        });
    }, [testQuestionCatalog, testQuestionFilter]);

    const testQuestionsBySection = useMemo(() => {
        return buildTestQuestionSectionCatalogGroups(visibleTestQuestionCatalog);
    }, [visibleTestQuestionCatalog]);

    const visibleTestQuestionSectionCount = useMemo(() => testQuestionsBySection.length, [testQuestionsBySection]);

    const catalogSectionsByChapter = useMemo(() => {
        const groups = new Map<number, SectionCatalogRow[]>();
        for (const row of visibleCatalog) {
            const list = groups.get(row.chapter_number) ?? [];
            list.push(row);
            groups.set(row.chapter_number, list);
        }
        return [...groups.entries()].sort(([a], [b]) => a - b);
    }, [visibleCatalog]);

    const chapterTitleByNumber = useMemo(() => {
        const map = new Map<number, string>();
        for (const ch of activeZybook?.chapters ?? []) {
            map.set(ch.number, ch.title);
        }
        return map;
    }, [activeZybook]);

    const selectedUnusedCount = useMemo(() => {
        return unusedCatalog.filter(r => selected.has(r.section_guid)).length;
    }, [unusedCatalog, selected]);

    const selectedAboutCount = useMemo(() => {
        return aboutPageCatalog.filter(r => selected.has(r.section_guid)).length;
    }, [aboutPageCatalog, selected]);

    const selectedTestQuestionSectionCount = useMemo(() => {
        return selectedTestQuestionSectionKeys.size;
    }, [selectedTestQuestionSectionKeys]);

    const selectedVisibleTestQuestionSectionCount = useMemo(() => {
        return testQuestionsBySection.filter(group => selectedTestQuestionSectionKeys.has(group.sectionKey)).length;
    }, [selectedTestQuestionSectionKeys, testQuestionsBySection]);

    const selectedVisibleCount = useMemo(() => {
        return visibleCatalog.filter(r => selected.has(r.section_guid)).length;
    }, [visibleCatalog, selected]);

    const selectedWithMaDocCount = useMemo(() => {
        return allCatalogRows.filter(r => {
            if (!selected.has(r.section_guid)) {
                return false;
            }
            return Boolean(getRowMaDocumentName(r, documentNamesBySectionKey, documentNamesByGuid)?.trim());
        }).length;
    }, [allCatalogRows, selected, documentNamesBySectionKey, documentNamesByGuid]);

    const loadCatalog = useCallback(async () => {
        const importZybookCode = zybookCodeInput.trim();
        if (!importZybookCode) {
            setLoadError('Enter a zybook code.');
            return;
        }

        setLoadingCatalog(true);
        setLoadError(null);
        setDeleteError(null);
        try {
            const importZybook = await getZybook(importZybookCode);
            if (!importZybook.development) {
                setLoadError(`${importZybookCode} is not a development book.`);
            }
            const catalogZybookCode = overrideCatalogZybookCode.trim() || importZybookCode;
            const catalogZybook = overrideCatalogZybookCode.trim() ? await getZybook(catalogZybookCode) : importZybook;
            let catalogSubject = catalogZybook.subjects[0];
            if (!catalogSubject?.subject_id) {
                catalogSubject = await getZybookSubject(catalogZybook.zybook_id);
                if (!catalogSubject?.subject_id) {
                    throw new Error('No subject found for import zybook.');
                }
            }
            const subject = await getSubject(catalogSubject.subject_id);
            const documents = await getMaDocumentsByZybook(importZybook.zybook_id);
            const mainSections = extractZybookSections(catalogZybook, catalogZybookCode);
            const maps = buildDocumentMapsBySection(documents ?? []);
            const catalogGuids = new Set(mainSections.map(r => r.section_guid));
            const catalogMaxChapter = Math.max(0, ...(catalogZybook.chapters ?? []).map(c => c.number));

            let unusedRows: SectionCatalogRow[] = [];
            let aboutRows: SectionCatalogRow[] = [];
            let testQuestionRows: TestQuestionCatalogRow[] = [];
            if (importUnusedFromAllDevBooks) {
                const devZybooks = await getSubjectDevZybooks(catalogSubject.subject_id);
                const devBookCodes = [...new Set([...devZybooks.map(z => z.zybook_code), catalogZybookCode])];
                const allDevBooks = await Promise.all(devBookCodes.map(code => getZybook(code)));
                const otherDevBooks = allDevBooks.filter(b => b.zybook_code !== catalogZybookCode && b.zybook_code !== importZybookCode);
                unusedRows = computeUnusedSectionsFromOtherDevBooks(otherDevBooks, catalogGuids, catalogMaxChapter);
            }

            if (includeAboutPages) {
                const aboutPagesZybookCode = overrideAboutPagesZybookCode.trim() || importZybookCode;
                const aboutPages = await getZybookAboutPages(aboutPagesZybookCode);
                aboutRows = computeAboutPageCatalog(aboutPages, importZybookCode);
            }

            if (includeTestQuestions) {
                const { test_questions: sameContentSourceTestQuestions, same_content_source_subject_count } =
                    await getSameContentSourceTestQuestions(catalogSubject.subject_id);
                const catalogSectionCoordsIndex = buildCatalogSectionCoordsIndex(catalogZybook);
                setSameContentSourceSubjectCount(same_content_source_subject_count);
                setAllTestQuestionCatalogRows(
                    sortTestQuestionCatalogRows(buildTestQuestionCatalogRows(sameContentSourceTestQuestions, catalogSectionCoordsIndex)),
                );
                testQuestionRows = sortTestQuestionCatalogRows(
                    buildTestQuestionCatalogRows(
                        dedupeZyServerTestQuestionsByGuid(sameContentSourceTestQuestions),
                        catalogSectionCoordsIndex,
                    ),
                );
            }

            const allSections = [...mainSections, ...unusedRows, ...aboutRows];
            setActiveSubject(subject);
            setActiveZybook(catalogZybook);
            setDocumentNamesBySectionKey(maps.namesBySection);
            setDocumentIdsBySectionKey(maps.idsBySection);
            setDocumentNamesByGuid(maps.namesByGuid);
            setDocumentIdsByGuid(maps.idsByGuid);
            setTestQuestionNamesBySectionKey(maps.testQuestionNamesBySection);
            setTestQuestionNamesByGuid(maps.testQuestionNamesByGuid);
            setCatalog(mainSections);
            setUnusedCatalog(unusedRows);
            setUnusedFilter('');
            setAboutPageCatalog(includeAboutPages ? aboutRows : []);
            setAboutFilter('');
            setTestQuestionCatalog(includeTestQuestions ? testQuestionRows : []);
            if (!includeTestQuestions) {
                setAllTestQuestionCatalogRows([]);
                setSameContentSourceSubjectCount(null);
            }
            setTestQuestionFilter('');
            setCatalogPanelOpen(true);
            setTestQuestionsPanelOpen(false);
            setUnusedPanelOpen(false);
            setAboutPanelOpen(false);
            setLastLoadedWithUnusedEnabled(importUnusedFromAllDevBooks);
            setLastLoadedWithTestQuestionsEnabled(includeTestQuestions);
            setLastLoadedWithAboutPagesEnabled(includeAboutPages);
            setActiveZybookCode(importZybookCode);
            setActiveZybookId(importZybook.zybook_id);
            setSelected(new Set(allSections.map(r => r.section_guid)));
            setSelectedTestQuestionSectionKeys(
                includeTestQuestions
                    ? new Set(
                          testQuestionRows
                              .map(row => testQuestionSectionKey(row.matched_chapter_number, row.matched_section_number))
                              .filter((key): key is string => key != null),
                      )
                    : new Set(),
            );
            setRowStates(Object.fromEntries(allSections.map(r => [r.section_guid, { status: 'idle', error: null } satisfies RowState])));
        } catch (e) {
            setLoadError((e as Error).message);
            setCatalog([]);
            setUnusedCatalog([]);
            setAboutPageCatalog([]);
            setTestQuestionCatalog([]);
            setAllTestQuestionCatalogRows([]);
            setSameContentSourceSubjectCount(null);
            setLastLoadedWithUnusedEnabled(null);
            setLastLoadedWithTestQuestionsEnabled(null);
            setLastLoadedWithAboutPagesEnabled(null);
            setSelectedTestQuestionSectionKeys(new Set());
            setDocumentNamesBySectionKey(new Map());
            setDocumentIdsBySectionKey(new Map());
            setDocumentNamesByGuid(new Map());
            setDocumentIdsByGuid(new Map());
            setTestQuestionNamesBySectionKey(new Map());
            setTestQuestionNamesByGuid(new Map());
            setActiveZybookCode(null);
            setActiveZybookId(null);
            setActiveZybook(null);
            setActiveSubject(null);
            console.error('Error loading catalog:', e);
        } finally {
            setLoadingCatalog(false);
        }
    }, [
        importUnusedFromAllDevBooks,
        includeAboutPages,
        includeTestQuestions,
        overrideAboutPagesZybookCode,
        overrideCatalogZybookCode,
        zybookCodeInput,
    ]);

    const handleCreateDevZybook = useCallback(async () => {
        const subjectId = Number.parseInt(devZybookSubjectIdInput.trim(), 10);
        const newZybookCode = devZybookNewCodeInput.trim();
        const title = devZybookTitleInput.trim();

        if (!Number.isFinite(subjectId) || subjectId <= 0 || !newZybookCode || !title) {
            setCreateDevZybookError('Enter a valid subject ID, title, and new zybook code.');
            return;
        }

        setCreateDevZybookRunning(true);
        setCreateDevZybookError(null);
        setCreateDevZybookSuccess(null);

        try {
            const result = await createDevZybook(subjectId, newZybookCode, title);
            setCreateDevZybookSuccess(`Created dev zybook ${result.zybook.zybook_code}.`);
            setZybookCodeInput(result.zybook.zybook_code);
        } catch (e) {
            setCreateDevZybookError((e as Error).message);
        } finally {
            setCreateDevZybookRunning(false);
        }
    }, [devZybookNewCodeInput, devZybookSubjectIdInput, devZybookTitleInput]);

    const selectAll = useCallback(() => {
        startTransition(() => {
            setSelected(new Set(visibleCatalog.map(r => r.section_guid)));
        });
    }, [visibleCatalog]);

    const clearCatalog = useCallback(() => {
        startTransition(() => {
            setSelected(prev => {
                const next = new Set(prev);
                for (const r of catalog) {
                    next.delete(r.section_guid);
                }
                return next;
            });
        });
    }, [catalog]);

    const clearAll = useCallback(() => {
        startTransition(() => {
            setSelected(new Set());
            setSelectedTestQuestionSectionKeys(new Set());
        });
    }, []);

    const selectAllUnused = useCallback(() => {
        startTransition(() => {
            setSelected(prev => {
                const next = new Set(prev);
                for (const r of visibleUnusedCatalog) {
                    next.add(r.section_guid);
                }
                return next;
            });
        });
    }, [visibleUnusedCatalog]);

    const clearUnused = useCallback(() => {
        startTransition(() => {
            setSelected(prev => {
                const next = new Set(prev);
                for (const r of unusedCatalog) {
                    next.delete(r.section_guid);
                }
                return next;
            });
        });
    }, [unusedCatalog]);

    const selectAllAbout = useCallback(() => {
        startTransition(() => {
            setSelected(prev => {
                const next = new Set(prev);
                for (const r of visibleAboutCatalog) {
                    next.add(r.section_guid);
                }
                return next;
            });
        });
    }, [visibleAboutCatalog]);

    const clearAbout = useCallback(() => {
        startTransition(() => {
            setSelected(prev => {
                const next = new Set(prev);
                for (const r of aboutPageCatalog) {
                    next.delete(r.section_guid);
                }
                return next;
            });
        });
    }, [aboutPageCatalog]);

    const selectAllTestQuestionSections = useCallback(() => {
        startTransition(() => {
            setSelectedTestQuestionSectionKeys(prev => {
                const next = new Set(prev);
                for (const group of testQuestionsBySection) {
                    next.add(group.sectionKey);
                }
                return next;
            });
        });
    }, [testQuestionsBySection]);

    const clearTestQuestionSections = useCallback(() => {
        startTransition(() => {
            setSelectedTestQuestionSectionKeys(prev => {
                const next = new Set(prev);
                for (const group of testQuestionsBySection) {
                    next.delete(group.sectionKey);
                }
                return next;
            });
        });
    }, [testQuestionsBySection]);

    const toggleTestQuestionSection = useCallback((sectionKey: string) => {
        startTransition(() => {
            setSelectedTestQuestionSectionKeys(prev => {
                const next = new Set(prev);
                if (next.has(sectionKey)) {
                    next.delete(sectionKey);
                } else {
                    next.add(sectionKey);
                }
                return next;
            });
        });
    }, []);

    const selectUnusedBySource = useCallback(
        (sourceCode: string) => {
            startTransition(() => {
                setSelected(prev => {
                    const next = new Set(prev);
                    for (const r of visibleUnusedCatalog) {
                        if (r.zybook_code === sourceCode) {
                            next.add(r.section_guid);
                        }
                    }
                    return next;
                });
            });
        },
        [visibleUnusedCatalog],
    );

    const chapterNumbers = useMemo(() => {
        const seen = new Set<number>();
        for (const r of visibleCatalog) {
            if (r.chapter_number > 0) {
                seen.add(r.chapter_number);
            }
        }
        return [...seen].sort((a, b) => a - b);
    }, [visibleCatalog]);

    const selectChapter = useCallback(
        (chapterNumber: number) => {
            startTransition(() => {
                setSelected(prev => {
                    const next = new Set(prev);
                    for (const r of visibleCatalog) {
                        if (r.chapter_number === chapterNumber) {
                            next.add(r.section_guid);
                        }
                    }
                    return next;
                });
            });
        },
        [visibleCatalog],
    );

    const toggleGuid = useCallback((guid: string) => {
        startTransition(() => {
            setSelected(prev => {
                const next = new Set(prev);
                if (next.has(guid)) {
                    next.delete(guid);
                } else {
                    next.add(guid);
                }
                return next;
            });
        });
    }, []);

    const importRowsForSingleGdoc = useCallback(
        async (gdocId: string, rows: SectionCatalogRow[], options?: { skipDocumentMapRefresh?: boolean }) => {
            setRowStates(prev => {
                const next = { ...prev };
                for (const r of rows) {
                    next[r.section_guid] = { status: 'importing', error: null };
                }
                return next;
            });

            const zybookCode = activeZybookCode;
            const zybookId = activeZybookId;
            if (!zybookCode || !zybookId) {
                setRowStates(prev => {
                    const next = { ...prev };
                    for (const r of rows) {
                        next[r.section_guid] = { status: 'failed', error: 'Missing zybook code.' };
                    }
                    return next;
                });
                return;
            }

            const subject = activeSubject;
            if (!subject) {
                setRowStates(prev => {
                    const next = { ...prev };
                    for (const r of rows) {
                        next[r.section_guid] = { status: 'failed', error: 'Missing subject.' };
                    }
                    return next;
                });
                return;
            }

            try {
                // Get zybooks JSON converted from Google Doc
                const convertOptions: GetConvertedGdocJsonOptions = {};
                if (shouldProcessWithVersion) {
                    convertOptions.contentVersion = overrideContentVersion.trim() || subject.version;
                    convertOptions.forceNoContentVersion = false;
                }
                const gdocJsonRaw = await getConvertedGdocJson(gdocId, convertOptions);
                const gdocJson = {
                    ...gdocJsonRaw,
                    sections: stripSubjectVersionFromSectionIds(gdocJsonRaw.sections, subject.version),
                };
                const chapterTitle = gdocJson.chapter_title;
                const shortChapterTitle = gdocJson.short_chapter_title;

                // Filter selected sections to convert
                const gdocSectionsToConvert = gdocJson.sections.filter((gdocJsonSection: any) =>
                    rows.some(selectedSection => isSectionMatch(selectedSection, gdocJsonSection)),
                );

                const zyversions = getZyVersionsFromRawZyXml(gdocJson.contents ?? '');
                const zyversionIdMap = buildZyVersionIdMap(zyversions);
                const devBookName = overrideSearchSnippetZybookCode.trim() || zybookCode;
                const devbookInfo = await zyAuthor.get<{ content_source: string; release_number: number }>(
                    `modern_authoring/get_devbook_info?devBookName=${encodeURIComponent(devBookName)}`,
                );
                const importReleaseId = parseReleaseId(releaseIdInput);
                const imageReleaseId = resolveImportImageReleaseId(releaseIdInput, devbookInfo.release_number);
                const imageSubject = devbookInfo.content_source;

                // Convert selected sections to ProseMirror JSON
                const convertedSections: GdocConvertedSection[] = await Promise.all(
                    gdocSectionsToConvert.map(async (section: any) => {
                        try {
                            const preparedSection = prepareSectionForImportConversion(section, {
                                subject: imageSubject,
                                releaseId: imageReleaseId,
                            });
                            const result = await convertParseResponseSection(preparedSection, 'json', {
                                subject,
                                devBookName: overrideSearchSnippetZybookCode.trim() || zybookCode,
                                zyversions,
                                zyversionIdMap,
                            });
                            if (!result) {
                                return {
                                    section,
                                    output: null,
                                    error: 'null result from convertParseResponseSection',
                                };
                            }
                            return { section: result.section, output: result.output as JSONContent, error: null };
                        } catch (error) {
                            console.error('Error converting section:', error);
                            return {
                                section,
                                output: null,
                                error: error instanceof Error ? error.message : String(error),
                            };
                        }
                    }),
                );

                // Match converted sections to selected sections
                const matched: { row: SectionCatalogRow; convertedSection: GdocConvertedSection }[] = [];
                const failed: { row: SectionCatalogRow; error: string }[] = [];
                for (const row of rows) {
                    const convertedSection = convertedSections.find(converted => isSectionMatch(row, converted.section));
                    if (!convertedSection) {
                        failed.push({ row, error: `Section ${row.section_guid} was not found in the Google Doc export.` });
                    } else if (convertedSection.error) {
                        failed.push({ row, error: `Section ${row.section_guid} error: ${convertedSection.error}` });
                    } else if (convertedSection.output) {
                        matched.push({ row, convertedSection });
                    }
                }

                // Handle failed rows
                if (failed.length > 0) {
                    const failedLogs: MaDocumentImportLog[] = [];
                    for (const { row, error } of failed) {
                        const existingName = getRowMaDocumentName(row, documentNamesBySectionKey, documentNamesByGuid);
                        failedLogs.push({
                            zybook_code: zybookCode,
                            chapter_number: row.chapter_number,
                            section_number: row.section_number,
                            section_guid: row.section_guid,
                            release_id: null,
                            gdoc_id: row.gdoc_id,
                            document_name: existingName?.trim() ?? null,
                            status: 'failed',
                            status_message: error,
                        });
                    }

                    if (failedLogs.length > 0) {
                        await createMaDocumentImportLogs(failedLogs);
                    }

                    setRowStates(prev => {
                        const next = { ...prev };
                        for (const { row, error } of failed) {
                            next[row.section_guid] = {
                                status: 'failed',
                                error,
                            };
                        }
                        return next;
                    });
                }

                if (matched.length === 0) {
                    return;
                }

                // Import documents
                const payload = matched.map<MaDocumentImportPayload>(({ row, convertedSection }) => {
                    const existingName = getRowMaDocumentName(row, documentNamesBySectionKey, documentNamesByGuid);
                    const documentName = existingName?.trim() ? existingName : `${subject.content_source}/${uuidv4()}`;
                    const resolvedChapterTitle = row.isAboutPage
                        ? (row.aboutChapterTitle ?? 'About')
                        : row.isUnused
                          ? (row.unusedChapterTitle ?? 'Unused')
                          : chapterTitle;
                    const resolvedShortChapterTitle = row.isAboutPage || row.isUnused ? null : shortChapterTitle;

                    // About pages versioned by section, so we can unwrap the top level zyVersionBlock nodes
                    if (row.isAboutPage) {
                        convertedSection.output = unwrapTopLevelZyVersionNodes(convertedSection.output as JSONContent);
                    }

                    return {
                        document_name: documentName,
                        chapter_number: row.chapter_number,
                        section_number: row.section_number,
                        chapter_title: processTitle(resolvedChapterTitle, subject.lab_language),
                        short_chapter_title: resolvedShortChapterTitle
                            ? processTitle(resolvedShortChapterTitle, subject.lab_language)
                            : null,
                        section_title: processTitle((convertedSection.section.full_title as string) || row.title, subject.lab_language),
                        short_section_title: processTitle(convertedSection.section.short_title ?? null, subject.lab_language),
                        section_guid: row.section_guid,
                        zybook_code: zybookCode,
                        tiptap_json: convertedSection.output as JSONContent,
                        gdoc_id: gdocId,
                        release_id: importReleaseId,
                        status: importReleaseId != null ? 'released' : 'dev',
                        type: 'section',
                    };
                });
                await importMaDocuments(payload, { overwriteExisting: true, noShareExisting: skipShareExisting });

                if (!options?.skipDocumentMapRefresh) {
                    const refreshed = await getMaDocumentsByZybook(zybookId);
                    const maps = buildDocumentMapsBySection(refreshed ?? []);
                    setDocumentNamesBySectionKey(maps.namesBySection);
                    setDocumentIdsBySectionKey(maps.idsBySection);
                    setDocumentNamesByGuid(maps.namesByGuid);
                    setDocumentIdsByGuid(maps.idsByGuid);
                    setTestQuestionNamesBySectionKey(maps.testQuestionNamesBySection);
                    setTestQuestionNamesByGuid(maps.testQuestionNamesByGuid);
                }

                setRowStates(prev => {
                    const next = { ...prev };
                    for (const { row } of matched) {
                        next[row.section_guid] = { status: 'success', error: null };
                    }
                    return next;
                });
            } catch (e) {
                const message = e instanceof Error ? e.message : String(e);
                setRowStates(prev => {
                    const next = { ...prev };
                    for (const r of rows) {
                        if (next[r.section_guid]?.status === 'importing') {
                            next[r.section_guid] = { status: 'failed', error: message };
                        }
                    }
                    return next;
                });

                // Create failed logs
                await createMaDocumentImportLogs(
                    rows.map(row => ({
                        zybook_code: zybookCode,
                        chapter_number: row.chapter_number,
                        section_number: row.section_number,
                        section_guid: row.section_guid,
                        release_id: null,
                        gdoc_id: gdocId,
                        document_name: null,
                        status: 'failed',
                        error_message: message,
                    })),
                );
            }
        },
        [
            activeSubject,
            activeZybookCode,
            activeZybookId,
            documentNamesBySectionKey,
            documentNamesByGuid,
            overrideContentVersion,
            overrideSearchSnippetZybookCode,
            releaseIdInput,
            shouldProcessWithVersion,
            skipShareExisting,
        ],
    );

    const importLabRows = useCallback(
        async (rows: SectionCatalogRow[]) => {
            if (!activeZybookCode || !activeSubject || !activeZybook || rows.length === 0) {
                return;
            }

            const zybookCode = activeZybookCode;
            const subject = activeSubject;
            const releaseId = parseReleaseId(releaseIdInput);
            const devBookName = overrideSearchSnippetZybookCode.trim() || zybookCode;
            const devbookInfo = await zyAuthor.get<{ content_source: string; release_number: number }>(
                `modern_authoring/get_devbook_info?devBookName=${encodeURIComponent(devBookName)}`,
            );
            const imageReleaseId = resolveImportImageReleaseId(releaseIdInput, devbookInfo.release_number);
            const imageSubject = devbookInfo.content_source;

            for (const row of rows) {
                setRowStates(prev => ({
                    ...prev,
                    [row.section_guid]: { status: 'importing', error: null },
                }));

                try {
                    const coords = contentCoords(row);
                    const zyServerSection = await getSection(coords.zybookCode, coords.chapterNumber, coords.sectionNumber);
                    const parserSection = {
                        ...zyServerSection,
                        id: zyServerSection.guid ?? row.section_guid,
                        chapter_number: String(coords.chapterNumber),
                        section_number: coords.sectionNumber,
                        full_title: zyServerSection.title,
                        title: zyServerSection.title,
                        short_title: zyServerSection.short_title ?? '',
                        activities: [],
                    };

                    // Merge instruction into lab content resource
                    if (parserSection.type === 'lab') {
                        const labContentResource = parserSection.content_resources[1];
                        labContentResource.payload = {
                            zybookCode,
                            chapterNumber: coords.chapterNumber,
                            sectionNumber: coords.sectionNumber,
                            markdown: parserSection.content_resources[0].payload.markdown ?? '',
                        };
                        parserSection.content_resources = [labContentResource];
                    }

                    const preparedSection = prepareSectionForImportConversion(parserSection, {
                        subject: imageSubject,
                        releaseId: imageReleaseId,
                    });
                    const converted = await convertParseResponseSection(preparedSection, 'json', {
                        subject,
                        devBookName,
                        zyversions: [],
                        zyversionIdMap: {},
                    });

                    if (!converted?.output) {
                        throw new Error('null result from convertParseResponseSection');
                    }

                    const chapter = row.isUnused ? null : activeZybook.chapters.find(c => c.number === row.chapter_number);
                    const existingName = getRowMaDocumentName(row, documentNamesBySectionKey, documentNamesByGuid);
                    const documentName = existingName?.trim() ? existingName : `${subject.content_source}/${uuidv4()}`;

                    // Make sure zyStudio node activityType is lab
                    const tiptapJson = converted.output as JSONContent;
                    tiptapJson.content = tiptapJson.content?.map(node => {
                        if (node.type === 'zyStudio' && node.attrs) {
                            node.attrs.activityType = 'lab';
                        }
                        return node;
                    });

                    await importMaDocuments(
                        [
                            {
                                document_name: documentName,
                                chapter_number: row.chapter_number,
                                section_number: row.section_number,
                                chapter_title: row.isUnused ? (row.unusedChapterTitle ?? 'Unused') : (chapter?.title ?? ''),
                                short_chapter_title: null,
                                section_title: (converted.section.full_title as string) || row.title,
                                short_section_title: converted.section.short_title ?? null,
                                section_guid: row.section_guid,
                                zybook_code: zybookCode,
                                tiptap_json: tiptapJson,
                                type: parserSection.type === 'it-lab' ? 'itlab' : 'lab',
                                release_id: releaseId,
                                status: releaseId != null ? 'released' : 'dev',
                            },
                        ],
                        { overwriteExisting: true, noShareExisting: skipShareExisting },
                    );

                    setRowStates(prev => ({
                        ...prev,
                        [row.section_guid]: { status: 'success', error: null },
                    }));
                } catch (error) {
                    const message = error instanceof Error ? error.message : String(error);
                    const existingName = getRowMaDocumentName(row, documentNamesBySectionKey, documentNamesByGuid);
                    await createMaDocumentImportLogs([
                        {
                            zybook_code: zybookCode,
                            chapter_number: row.chapter_number,
                            section_number: row.section_number,
                            section_guid: row.section_guid,
                            release_id: null,
                            gdoc_id: row.gdoc_id,
                            document_name: existingName?.trim() ?? null,
                            status: 'failed',
                            status_message: message,
                        },
                    ]);
                    setRowStates(prev => ({
                        ...prev,
                        [row.section_guid]: { status: 'failed', error: message },
                    }));
                }
            }
        },
        [
            activeSubject,
            activeZybook,
            activeZybookCode,
            documentNamesBySectionKey,
            documentNamesByGuid,
            overrideSearchSnippetZybookCode,
            releaseIdInput,
            skipShareExisting,
        ],
    );

    const importGdocRows = useCallback(
        async (rows: SectionCatalogRow[]) => {
            if (rows.length === 0) {
                return;
            }
            const byGdoc = new Map<string, SectionCatalogRow[]>();
            for (const row of rows) {
                const list = byGdoc.get(row.gdoc_id) ?? [];
                list.push(row);
                byGdoc.set(row.gdoc_id, list);
            }
            for (const [gdocId, gdocRows] of byGdoc) {
                await importRowsForSingleGdoc(gdocId, gdocRows, { skipDocumentMapRefresh: true });
            }
        },
        [importRowsForSingleGdoc],
    );

    const importTestQuestions = useCallback(
        async (
            idsBySection: Map<string, number>,
            testQuestionNamesBySection: Map<string, string[]>,
            testQuestionNamesByGuid: Map<string, string[]>,
        ) => {
            if (!activeZybookCode || !activeSubject || activeZybookId == null) {
                return;
            }

            const selectedRows = allTestQuestionCatalogRows.filter(row => {
                const sectionKey = testQuestionSectionKey(row.matched_chapter_number, row.matched_section_number);
                return sectionKey != null && selectedTestQuestionSectionKeys.has(sectionKey);
            });
            if (selectedRows.length === 0) {
                return;
            }

            if (!sameContentSourceSubjectCount) {
                setTestQuestionImportStates(prev => {
                    const next = { ...prev };
                    for (const sectionKey of selectedTestQuestionSectionKeys) {
                        next[sectionKey] = {
                            status: 'failed',
                            error: 'Same content source subject count is unavailable.',
                        };
                    }
                    return next;
                });
                return;
            }

            const groups = groupTestQuestionsByImportSection(selectedRows);
            const skippedSectionKeys = new Set(
                [...selectedTestQuestionSectionKeys].filter(sectionKey => {
                    return !groups.some(group => group.groupKey === sectionKey);
                }),
            );

            setTestQuestionImportStates(prev => {
                const next = { ...prev };
                for (const group of groups) {
                    next[group.groupKey] = { status: 'pending', error: null };
                }
                for (const sectionKey of skippedSectionKeys) {
                    next[sectionKey] = {
                        status: 'failed',
                        error: 'No matching section on catalog zybook.',
                    };
                }
                return next;
            });

            for (const group of groups) {
                setTestQuestionImportStates(prev => ({
                    ...prev,
                    [group.groupKey]: { status: 'importing', error: null },
                }));

                const sectionMaDocumentId = idsBySection.get(maSectionKey(group.chapter_number, group.section_number));
                if (sectionMaDocumentId == null) {
                    const error = 'Section MA document required before importing test questions.';
                    setTestQuestionImportStates(prev => ({
                        ...prev,
                        [group.groupKey]: { status: 'failed', error },
                    }));
                    continue;
                }

                const chapterTitle =
                    activeZybook?.chapters.find(c => c.number === group.chapter_number)?.title ?? `Chapter ${group.chapter_number}`;

                try {
                    const existingName = getTestQuestionDocumentName(
                        group.chapter_number,
                        group.section_number,
                        group.section_guid,
                        testQuestionNamesBySection,
                        testQuestionNamesByGuid,
                    );
                    const payload = buildTestQuestionImportPayload(group, {
                        importZybookCode: activeZybookCode,
                        contentSource: activeSubject.content_source,
                        sectionMaDocumentId,
                        chapterTitle,
                        existingTestQuestionDocumentName: existingName,
                        sameContentSourceSubjectCount,
                        releaseId: parseReleaseId(releaseIdInput),
                    });
                    await importMaDocuments([payload], { overwriteExisting: true, noShareExisting: skipShareExisting });
                    setTestQuestionImportStates(prev => ({
                        ...prev,
                        [group.groupKey]: { status: 'success', error: null },
                    }));
                } catch (error) {
                    const message = error instanceof Error ? error.message : String(error);
                    setTestQuestionImportStates(prev => ({
                        ...prev,
                        [group.groupKey]: { status: 'failed', error: message },
                    }));
                }
            }
        },
        [
            activeSubject,
            activeZybook,
            activeZybookCode,
            activeZybookId,
            allTestQuestionCatalogRows,
            releaseIdInput,
            sameContentSourceSubjectCount,
            selectedTestQuestionSectionKeys,
            skipShareExisting,
        ],
    );

    const performImport = useCallback(async () => {
        if (!activeZybookCode || activeZybookId == null) {
            return;
        }
        const toImport = allCatalogRows.filter(r => selected.has(r.section_guid));
        const hasSelectedTestQuestions = selectedTestQuestionSectionKeys.size > 0;
        if (toImport.length === 0 && !hasSelectedTestQuestions) {
            return;
        }

        const labRows = toImport.filter(r => r.hasLabs && !r.isAboutPage);
        const gdocRows = toImport.filter(r => !r.hasLabs);
        const missingGdoc = gdocRows.filter(r => !r.gdoc_id);
        const validGdocRows = gdocRows.filter(r => r.gdoc_id);

        setRowStates(prev => {
            const next = { ...prev };
            for (const r of toImport) {
                next[r.section_guid] = { status: 'pending', error: null };
            }
            return next;
        });

        if (toImport.length > 0 && missingGdoc.length > 0) {
            const error = 'Section has no gdoc_id.';
            setRowStates(prev => {
                const next = { ...prev };
                for (const r of missingGdoc) {
                    next[r.section_guid] = { status: 'failed', error };
                }
                return next;
            });
            await createMaDocumentImportLogs(
                missingGdoc.map(row => {
                    const existingName = getRowMaDocumentName(row, documentNamesBySectionKey, documentNamesByGuid);
                    return {
                        zybook_code: activeZybookCode,
                        chapter_number: row.chapter_number,
                        section_number: row.section_number,
                        section_guid: row.section_guid,
                        release_id: null,
                        gdoc_id: row.gdoc_id,
                        document_name: existingName?.trim() ?? null,
                        status: 'failed' as const,
                        status_message: error,
                    };
                }),
            );
        }

        setImportRunning(true);
        try {
            if (labRows.length > 0) {
                await importLabRows(labRows);
            }
            if (validGdocRows.length > 0) {
                await importGdocRows(validGdocRows);
            }

            let latestIdsBySection = documentIdsBySectionKey;
            let latestTestQuestionNamesBySection = testQuestionNamesBySectionKey;
            let latestTestQuestionNamesByGuid = testQuestionNamesByGuid;
            if (toImport.length > 0 || hasSelectedTestQuestions) {
                const refreshed = await getMaDocumentsByZybook(activeZybookId);
                const maps = buildDocumentMapsBySection(refreshed ?? []);
                setDocumentNamesBySectionKey(maps.namesBySection);
                latestIdsBySection = maps.idsBySection;
                setDocumentIdsBySectionKey(maps.idsBySection);
                setDocumentNamesByGuid(maps.namesByGuid);
                setDocumentIdsByGuid(maps.idsByGuid);
                latestTestQuestionNamesBySection = maps.testQuestionNamesBySection;
                latestTestQuestionNamesByGuid = maps.testQuestionNamesByGuid;
                setTestQuestionNamesBySectionKey(maps.testQuestionNamesBySection);
                setTestQuestionNamesByGuid(maps.testQuestionNamesByGuid);
            }

            if (hasSelectedTestQuestions) {
                await importTestQuestions(latestIdsBySection, latestTestQuestionNamesBySection, latestTestQuestionNamesByGuid);
            }

            if (toImport.length > 0 || hasSelectedTestQuestions) {
                const refreshed = await getMaDocumentsByZybook(activeZybookId);
                const maps = buildDocumentMapsBySection(refreshed ?? []);
                setDocumentNamesBySectionKey(maps.namesBySection);
                setDocumentIdsBySectionKey(maps.idsBySection);
                setDocumentNamesByGuid(maps.namesByGuid);
                setDocumentIdsByGuid(maps.idsByGuid);
                setTestQuestionNamesBySectionKey(maps.testQuestionNamesBySection);
                setTestQuestionNamesByGuid(maps.testQuestionNamesByGuid);
            }

            if (toImport.length > 0 || hasSelectedTestQuestions) {
                await zyAuthor.post(`modern_authoring/${activeZybookId}/ref_resolve`, {});
            }
        } finally {
            setImportRunning(false);
        }
    }, [
        activeZybookCode,
        activeZybookId,
        allCatalogRows,
        documentIdsBySectionKey,
        documentNamesBySectionKey,
        documentNamesByGuid,
        testQuestionNamesBySectionKey,
        testQuestionNamesByGuid,
        importGdocRows,
        importLabRows,
        importTestQuestions,
        selected,
        selectedTestQuestionSectionKeys,
    ]);

    const performDeleteSelected = useCallback(async () => {
        if (!activeZybookCode) {
            return;
        }
        const selectedRows = allCatalogRows.filter(r => selected.has(r.section_guid));
        const withMaDoc = selectedRows.filter(r =>
            Boolean(getRowMaDocumentName(r, documentNamesBySectionKey, documentNamesByGuid)?.trim()),
        );
        if (withMaDoc.length === 0) {
            return;
        }
        const documents = withMaDoc.map(r => ({
            name: getRowMaDocumentName(r, documentNamesBySectionKey, documentNamesByGuid)!.trim(),
        }));
        setDeleteRunning(true);
        setDeleteError(null);
        try {
            const deleteResult = await deleteMaDocuments(documents, { delete_tiptap: true });
            const deletedNames = new Set(deleteResult.deleted_names ?? []);
            const sectionKeysToRemove = new Set<string>();
            const guidKeysToRemove = new Set<string>();
            for (const r of withMaDoc) {
                const docName = getRowMaDocumentName(r, documentNamesBySectionKey, documentNamesByGuid)?.trim();
                if (docName && deletedNames.has(docName)) {
                    if (r.isUnused) {
                        guidKeysToRemove.add(r.section_guid);
                    } else {
                        sectionKeysToRemove.add(maSectionKey(r.chapter_number, r.section_number));
                    }
                }
            }
            if (!deleteResult.success && deleteResult.failed_documents?.length) {
                const detail = deleteResult.failed_documents.map(f => `${f.name}: ${f.error}`).join('; ');
                setDeleteError(`TipTap delete failed for one or more documents: ${detail}`);
            }
            setDocumentNamesBySectionKey(prev => {
                const next = new Map(prev);
                for (const key of sectionKeysToRemove) {
                    next.delete(key);
                }
                return next;
            });
            setDocumentIdsBySectionKey(prev => {
                const next = new Map(prev);
                for (const key of sectionKeysToRemove) {
                    next.delete(key);
                }
                return next;
            });
            setDocumentNamesByGuid(prev => {
                const next = new Map(prev);
                for (const key of guidKeysToRemove) {
                    next.delete(key);
                }
                return next;
            });
            setDocumentIdsByGuid(prev => {
                const next = new Map(prev);
                for (const key of guidKeysToRemove) {
                    next.delete(key);
                }
                return next;
            });
            setRowStates(prev => {
                const next = { ...prev };
                for (const r of withMaDoc) {
                    const removed = r.isUnused
                        ? guidKeysToRemove.has(r.section_guid)
                        : sectionKeysToRemove.has(maSectionKey(r.chapter_number, r.section_number));
                    if (removed) {
                        next[r.section_guid] = { status: 'idle', error: null };
                    }
                }
                return next;
            });
        } catch (e) {
            setDeleteError((e as Error).message);
        } finally {
            setDeleteRunning(false);
        }
    }, [activeZybookCode, allCatalogRows, selected, documentNamesBySectionKey, documentNamesByGuid]);

    const selectedTestQuestionGroupCount = useMemo(() => {
        return selectedTestQuestionSectionKeys.size;
    }, [selectedTestQuestionSectionKeys]);

    const selectedCount = selected.size;
    const hasImportSelection = selectedCount > 0 || selectedTestQuestionSectionKeys.size > 0;
    const busy = importRunning || deleteRunning || createDevZybookRunning;

    return (
        <div className="flex h-[calc(100svh-49px)] min-h-0 flex-col overflow-hidden bg-muted/30">
            <main className="mx-auto flex w-full max-w-4xl flex-1 min-h-0 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden overscroll-y-contain px-6 py-8">
                    <div className="mx-auto max-w-4xl">
                        <h1 className="text-2xl font-semibold tracking-tight">Import zybook</h1>
                        <p className="text-muted-foreground mt-1 text-sm">
                            Load sections by dev book zybook code. Overwrite existing TipTap documents if the document name already exists.
                        </p>
                        <p className="mt-2">
                            <button
                                type="button"
                                className="text-primary text-sm underline-offset-4 hover:underline"
                                onClick={() => setImportLogsDialogOpen(true)}
                            >
                                View import logs
                            </button>
                        </p>
                    </div>
                    <div className="rounded-md border border-border bg-background">
                        <Collapsible
                            open={createDevZybookOpen}
                            onOpenChange={setCreateDevZybookOpen}
                        >
                            <CollapsibleTrigger className="flex w-full items-start justify-between gap-3 p-4 text-left">
                                <div className="space-y-1">
                                    <h2 className="text-sm font-medium">Create dev zybook</h2>
                                    <p className="text-muted-foreground text-xs">
                                        Create an empty development zybook and register it as additional dev content on a subject.
                                    </p>
                                </div>
                                {createDevZybookOpen ? (
                                    <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                                ) : (
                                    <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                                )}
                            </CollapsibleTrigger>
                            <CollapsibleContent className="space-y-3 border-t px-4 pb-4 pt-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="dev-zybook-title">Zybook title</Label>
                                    <input
                                        id="dev-zybook-title"
                                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        placeholder="e.g. Programming in Python 2 (Dev)"
                                        value={devZybookTitleInput}
                                        onChange={e => setDevZybookTitleInput(e.target.value)}
                                        disabled={busy}
                                    />
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="dev-zybook-subject-id">Subject ID</Label>
                                        <input
                                            id="dev-zybook-subject-id"
                                            type="number"
                                            min={1}
                                            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            placeholder={activeSubject ? String(activeSubject.subject_id) : 'e.g. 42'}
                                            value={devZybookSubjectIdInput}
                                            onChange={e => setDevZybookSubjectIdInput(e.target.value)}
                                            disabled={busy}
                                        />
                                        {activeSubject && (
                                            <p className="text-muted-foreground text-xs">
                                                Loaded subject: {activeSubject.display_name} ({activeSubject.subject_id})
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="dev-zybook-new-code">New dev zybook code</Label>
                                        <input
                                            id="dev-zybook-new-code"
                                            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            placeholder="e.g. ProgrammingInPython2Dev"
                                            value={devZybookNewCodeInput}
                                            onChange={e => setDevZybookNewCodeInput(e.target.value)}
                                            disabled={busy}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                        onClick={() => void handleCreateDevZybook()}
                                        disabled={busy}
                                    >
                                        {createDevZybookRunning ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Creating…
                                            </>
                                        ) : (
                                            'Create dev zybook'
                                        )}
                                    </Button>
                                </div>
                                {createDevZybookError && <p className="text-destructive text-sm">{createDevZybookError}</p>}
                                {createDevZybookSuccess && <p className="text-sm text-green-600">{createDevZybookSuccess}</p>}
                            </CollapsibleContent>
                        </Collapsible>
                    </div>

                    <section className="space-y-3 rounded-md border border-border bg-background p-4">
                        <div className="space-y-0.5">
                            <h2 className="text-sm font-medium">Load catalog</h2>
                            <p className="text-muted-foreground text-xs">Load sections from a dev zybook to import.</p>
                        </div>
                        <div className="space-y-2.5">
                            <div className="space-y-1">
                                <Label
                                    htmlFor="zybook-code"
                                    className="text-xs"
                                >
                                    Import into this dev zybook
                                </Label>
                                <input
                                    id="zybook-code"
                                    className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    placeholder="e.g. ProgrammingInPython2Dev"
                                    value={zybookCodeInput}
                                    onChange={e => setZybookCodeInput(e.target.value)}
                                    disabled={loadingCatalog || busy}
                                />
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2">
                                <div className="space-y-1">
                                    <Label
                                        htmlFor="override-catalog-zybook-code"
                                        className="text-xs"
                                    >
                                        Load sections from this zybook (optional)
                                    </Label>
                                    <input
                                        id="override-catalog-zybook-code"
                                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        placeholder="Defaults to import code"
                                        value={overrideCatalogZybookCode}
                                        onChange={e => setOverrideCatalogZybookCode(e.target.value)}
                                        disabled={loadingCatalog || busy}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label
                                        htmlFor="override-search-snippet-zybook-code"
                                        className="text-xs"
                                    >
                                        Search XML snippet from this zybook (optional)
                                    </Label>
                                    <input
                                        id="override-search-snippet-zybook-code"
                                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        placeholder="Defaults to import code"
                                        value={overrideSearchSnippetZybookCode}
                                        onChange={e => setOverrideSearchSnippetZybookCode(e.target.value)}
                                        disabled={loadingCatalog || busy}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label
                                        htmlFor="override-about-pages-zybook-code"
                                        className="text-xs"
                                    >
                                        Search about pages from this zybook (optional)
                                    </Label>
                                    <input
                                        id="override-about-pages-zybook-code"
                                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        placeholder="Defaults to import code"
                                        value={overrideAboutPagesZybookCode}
                                        onChange={e => setOverrideAboutPagesZybookCode(e.target.value)}
                                        disabled={loadingCatalog || busy}
                                    />
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <Checkbox
                                    id="import-unused-from-all-dev-books"
                                    checked={importUnusedFromAllDevBooks}
                                    onCheckedChange={value => setImportUnusedFromAllDevBooks(value === true)}
                                    className="mt-0.5"
                                    disabled={loadingCatalog || busy}
                                />
                                <Label
                                    htmlFor="import-unused-from-all-dev-books"
                                    className="cursor-pointer text-xs font-normal leading-snug"
                                >
                                    Include unused sections from all dev books of the subject
                                </Label>
                            </div>
                            <div className="flex items-start gap-2">
                                <Checkbox
                                    id="include-test-questions"
                                    checked={includeTestQuestions}
                                    onCheckedChange={value => setIncludeTestQuestions(value === true)}
                                    className="mt-0.5"
                                    disabled={loadingCatalog || busy}
                                />
                                <Label
                                    htmlFor="include-test-questions"
                                    className="cursor-pointer text-xs font-normal leading-snug"
                                >
                                    Include test questions
                                </Label>
                            </div>
                            <div className="flex items-start gap-2">
                                <Checkbox
                                    id="include-about-pages"
                                    checked={includeAboutPages}
                                    onCheckedChange={value => setIncludeAboutPages(value === true)}
                                    className="mt-0.5"
                                    disabled={loadingCatalog || busy}
                                />
                                <Label
                                    htmlFor="include-about-pages"
                                    className="cursor-pointer text-xs font-normal leading-snug"
                                >
                                    Include about pages
                                </Label>
                            </div>
                            <div className="flex items-start gap-2">
                                <Checkbox
                                    id="skip-share-existing"
                                    checked={skipShareExisting}
                                    onCheckedChange={value => setSkipShareExisting(value === true)}
                                    className="mt-0.5"
                                    disabled={loadingCatalog || busy}
                                />
                                <Label
                                    htmlFor="skip-share-existing"
                                    className="cursor-pointer text-xs font-normal leading-snug"
                                >
                                    Skip sharing existing document on import
                                </Label>
                            </div>
                            <div className="space-y-1">
                                <Label
                                    htmlFor="release-id"
                                    className="text-xs"
                                >
                                    Release ID (optional)
                                </Label>
                                <input
                                    id="release-id"
                                    type="number"
                                    className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full max-w-[168px] rounded-md border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    placeholder="None"
                                    value={releaseIdInput}
                                    onChange={e => setReleaseIdInput(e.target.value)}
                                    disabled={loadingCatalog || busy}
                                />
                            </div>
                            {loadError && <p className="text-destructive text-xs">{loadError}</p>}
                            {catalog.length > 0 &&
                                lastLoadedWithUnusedEnabled !== null &&
                                importUnusedFromAllDevBooks !== lastLoadedWithUnusedEnabled && (
                                    <p className="text-muted-foreground text-xs">Reload sections to refresh the unused list.</p>
                                )}
                            {catalog.length > 0 &&
                                lastLoadedWithTestQuestionsEnabled !== null &&
                                includeTestQuestions !== lastLoadedWithTestQuestionsEnabled && (
                                    <p className="text-muted-foreground text-xs">Reload sections to refresh the test questions list.</p>
                                )}
                            {catalog.length > 0 &&
                                lastLoadedWithAboutPagesEnabled !== null &&
                                includeAboutPages !== lastLoadedWithAboutPagesEnabled && (
                                    <p className="text-muted-foreground text-xs">Reload sections to refresh the about pages list.</p>
                                )}
                        </div>
                        <div className="flex justify-end border-border pt-3">
                            <Button
                                onClick={() => void loadCatalog()}
                                disabled={loadingCatalog || busy}
                                size="sm"
                            >
                                {loadingCatalog ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Loading…
                                    </>
                                ) : (
                                    'Load sections'
                                )}
                            </Button>
                        </div>
                    </section>

                    {activeSubject && (
                        <section className="space-y-3 rounded-md border border-border bg-background p-4">
                            <h2 className="text-sm font-medium">GDoc conversion</h2>
                            <div className="flex items-start gap-3">
                                <Checkbox
                                    id="should-process-with-version"
                                    checked={shouldProcessWithVersion}
                                    onCheckedChange={value => setShouldProcessWithVersion(value === true)}
                                    disabled={busy}
                                    className="mt-0.5"
                                />
                                <div className="space-y-1">
                                    <Label
                                        htmlFor="should-process-with-version"
                                        className="cursor-pointer font-normal leading-snug"
                                    >
                                        Process with content version
                                    </Label>
                                    <p className="text-muted-foreground text-xs">
                                        When off, the backend parses the gdoc without a content version. When on, a version is sent
                                        (override below, or the loaded subject&apos;s version if the override is empty).
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="override-content-version">Override content version</Label>
                                <input
                                    id="override-content-version"
                                    className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full max-w-md rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    placeholder={activeSubject?.version ? `${activeSubject.version}` : 'e.g. 1.0'}
                                    value={overrideContentVersion}
                                    onChange={e => setOverrideContentVersion(e.target.value)}
                                    disabled={busy || !shouldProcessWithVersion}
                                    aria-disabled={busy || !shouldProcessWithVersion}
                                />
                            </div>
                        </section>
                    )}

                    {(catalog.length > 0 || testQuestionCatalog.length > 0 || aboutPageCatalog.length > 0) && (
                        <div className="space-y-4">
                            <div className="flex flex-wrap items-center justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={clearAll}
                                    disabled={busy || !hasImportSelection}
                                >
                                    Clear all
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => setImportDialogOpen(true)}
                                    disabled={busy || !hasImportSelection}
                                >
                                    {importRunning ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Importing…
                                        </>
                                    ) : (
                                        'Import selected'
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setDeleteDialogOpen(true)}
                                    disabled={busy || selectedWithMaDocCount === 0}
                                >
                                    {deleteRunning ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Deleting…
                                        </>
                                    ) : (
                                        'Delete selected'
                                    )}
                                </Button>
                            </div>
                            {deleteError && <p className="text-destructive text-sm">{deleteError}</p>}

                            <section>
                                <Collapsible
                                    open={catalogPanelOpen}
                                    onOpenChange={setCatalogPanelOpen}
                                >
                                    <div className="overflow-hidden rounded-lg border-2 border-blue-500/45 bg-card shadow-md ring-1 ring-blue-500/15">
                                        <CollapsibleTrigger className="flex w-full cursor-pointer items-start justify-between gap-3 border-b border-blue-500/20 bg-blue-500/5 px-4 py-3.5 text-left transition-colors hover:bg-blue-500/10">
                                            <div className="space-y-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <h2 className="text-base font-semibold">Catalog sections</h2>
                                                    <Badge
                                                        variant="outline"
                                                        className="border-blue-500/40 bg-blue-500/10 text-blue-900 dark:text-blue-200"
                                                    >
                                                        {catalog.length}
                                                    </Badge>
                                                    {selectedVisibleCount > 0 && (
                                                        <Badge variant="default">{selectedVisibleCount} selected</Badge>
                                                    )}
                                                </div>
                                                <p className="text-muted-foreground text-xs">
                                                    Sections from the catalog zybook used for chapter and section ordering.
                                                </p>
                                            </div>
                                            {catalogPanelOpen ? (
                                                <ChevronDown className="mt-1 size-4 shrink-0 text-muted-foreground" />
                                            ) : (
                                                <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
                                            )}
                                        </CollapsibleTrigger>
                                        <CollapsibleContent className="space-y-4 bg-background px-4 pb-4 pt-3">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                                                    <p className="text-muted-foreground text-sm">
                                                        {selectedVisibleCount} of {visibleCatalog.length} selected
                                                        {showOnlyWithoutMaDoc ? (
                                                            <span className="text-muted-foreground"> ({catalog.length} total)</span>
                                                        ) : null}
                                                        {unusedCatalog.length > 0 ? (
                                                            <span className="text-muted-foreground">
                                                                {' '}
                                                                ({selectedUnusedCount} of {unusedCatalog.length} unused selected)
                                                            </span>
                                                        ) : null}
                                                        {selectedWithMaDocCount > 0 ? (
                                                            <span className="text-muted-foreground">
                                                                {' '}
                                                                ({selectedWithMaDocCount} with MA doc)
                                                            </span>
                                                        ) : null}
                                                    </p>
                                                    <div className="flex items-center gap-2">
                                                        <Checkbox
                                                            id="show-only-without-ma-doc"
                                                            checked={showOnlyWithoutMaDoc}
                                                            onCheckedChange={value => setShowOnlyWithoutMaDoc(value === true)}
                                                            disabled={busy}
                                                        />
                                                        <Label
                                                            htmlFor="show-only-without-ma-doc"
                                                            className="cursor-pointer text-sm font-normal"
                                                        >
                                                            Without MA doc only
                                                        </Label>
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={selectAll}
                                                        disabled={busy}
                                                    >
                                                        Select all
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={clearCatalog}
                                                        disabled={busy || selectedVisibleCount === 0}
                                                    >
                                                        Clear all
                                                    </Button>
                                                    <Select
                                                        key={chapterSelectKey}
                                                        disabled={busy || chapterNumbers.length === 0}
                                                        onValueChange={value => {
                                                            selectChapter(Number(value));
                                                            setChapterSelectKey(k => k + 1);
                                                        }}
                                                    >
                                                        <SelectTrigger
                                                            className="h-9 w-[168px]"
                                                            aria-label="Select all sections in a chapter"
                                                        >
                                                            <SelectValue placeholder="Select chapter…" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {chapterNumbers.map(ch => (
                                                                <SelectItem
                                                                    key={ch}
                                                                    value={String(ch)}
                                                                >
                                                                    Chapter {ch}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>

                                            {visibleCatalog.length === 0 ? (
                                                <p className="text-muted-foreground text-sm">
                                                    {showOnlyWithoutMaDoc
                                                        ? 'All sections already have an MA document.'
                                                        : 'No sections to show.'}
                                                </p>
                                            ) : (
                                                <div className="space-y-3">
                                                    {catalogSectionsByChapter.map(([chapterNumber, rows]) => {
                                                        const chapterTitle = chapterTitleByNumber.get(chapterNumber);
                                                        const selectedInChapter = rows.filter(r => selected.has(r.section_guid)).length;
                                                        return (
                                                            <Collapsible
                                                                key={chapterNumber}
                                                                defaultOpen={false}
                                                            >
                                                                <div className="flex w-full items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
                                                                    <CollapsibleTrigger className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left">
                                                                        <span className="text-sm font-medium">
                                                                            Chapter {chapterNumber}
                                                                            {chapterTitle ? `: ${chapterTitle}` : ''}
                                                                        </span>
                                                                        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                                                                    </CollapsibleTrigger>
                                                                    <span className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs">
                                                                        {selectedInChapter}/{rows.length} selected
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="h-7 px-2"
                                                                            disabled={busy}
                                                                            onClick={() => selectChapter(chapterNumber)}
                                                                        >
                                                                            Select all
                                                                        </Button>
                                                                    </span>
                                                                </div>
                                                                <CollapsibleContent className="pt-2">
                                                                    <div className="rounded-md border bg-background">
                                                                        <Table>
                                                                            <TableHeader>
                                                                                <TableRow>
                                                                                    <TableHead className="w-12" />
                                                                                    <TableHead className="w-14 text-right">Ch.</TableHead>
                                                                                    <TableHead className="w-14 text-right">Sec.</TableHead>
                                                                                    <TableHead>Title</TableHead>
                                                                                    <TableHead className="w-14">GUID</TableHead>
                                                                                    <TableHead className="w-30 text-center">
                                                                                        MA doc
                                                                                    </TableHead>
                                                                                    <TableHead className="hidden md:table-cell">
                                                                                        gdoc
                                                                                    </TableHead>
                                                                                    <TableHead className="w-36">Status</TableHead>
                                                                                </TableRow>
                                                                            </TableHeader>
                                                                            <TableBody>
                                                                                {rows.map(row => {
                                                                                    const st = rowStates[row.section_guid] ?? {
                                                                                        status: 'idle',
                                                                                        error: null,
                                                                                    };
                                                                                    const maDocumentName =
                                                                                        getRowMaDocumentName(
                                                                                            row,
                                                                                            documentNamesBySectionKey,
                                                                                            documentNamesByGuid,
                                                                                        )?.trim() || null;
                                                                                    const maDocumentId =
                                                                                        getRowMaDocumentId(
                                                                                            row,
                                                                                            documentIdsBySectionKey,
                                                                                            documentIdsByGuid,
                                                                                        ) ?? null;
                                                                                    return (
                                                                                        <ImportCatalogTableRow
                                                                                            key={row.section_guid}
                                                                                            row={row}
                                                                                            isSelected={selected.has(row.section_guid)}
                                                                                            maDocumentName={maDocumentName}
                                                                                            maDocumentId={maDocumentId}
                                                                                            rowState={st}
                                                                                            checkboxDisabled={
                                                                                                busy ||
                                                                                                st.status === 'pending' ||
                                                                                                st.status === 'importing'
                                                                                            }
                                                                                            onToggleGuid={toggleGuid}
                                                                                            documentZybookCode={
                                                                                                activeZybookCode ?? undefined
                                                                                            }
                                                                                        />
                                                                                    );
                                                                                })}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </div>
                                                                </CollapsibleContent>
                                                            </Collapsible>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </CollapsibleContent>
                                    </div>
                                </Collapsible>
                            </section>

                            {unusedCatalog.length > 0 && (
                                <section>
                                    <Collapsible
                                        open={unusedPanelOpen}
                                        onOpenChange={setUnusedPanelOpen}
                                    >
                                        <div className="overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/35 bg-card shadow-md ring-1 ring-muted-foreground/10">
                                            <CollapsibleTrigger className="flex w-full cursor-pointer items-start justify-between gap-3 border-b border-border bg-muted/40 px-4 py-3.5 text-left transition-colors hover:bg-muted/60">
                                                <div className="space-y-1">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <h2 className="text-base font-semibold">Unused sections from other dev books</h2>
                                                        <Badge
                                                            variant="outline"
                                                            className="border-muted-foreground/30 bg-muted text-muted-foreground"
                                                        >
                                                            {unusedCatalog.length}
                                                        </Badge>
                                                        {selectedUnusedCount > 0 && (
                                                            <Badge variant="default">{selectedUnusedCount} selected</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-muted-foreground text-xs">
                                                        Sections in other subject dev books whose GUID is not in the catalog zybook.
                                                        Imported with negative chapter and section numbers.
                                                    </p>
                                                </div>
                                                {unusedPanelOpen ? (
                                                    <ChevronDown className="mt-1 size-4 shrink-0 text-muted-foreground" />
                                                ) : (
                                                    <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
                                                )}
                                            </CollapsibleTrigger>
                                            <CollapsibleContent className="space-y-3 bg-background px-4 pb-4 pt-3">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={selectAllUnused}
                                                        disabled={busy || visibleUnusedCatalog.length === 0}
                                                    >
                                                        Select all unused
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={clearUnused}
                                                        disabled={busy || selectedUnusedCount === 0}
                                                    >
                                                        Clear unused
                                                    </Button>
                                                    <input
                                                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 min-w-[200px] flex-1 rounded-md border px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                        placeholder="Filter by title, GUID, or source book…"
                                                        value={unusedFilter}
                                                        onChange={e => setUnusedFilter(e.target.value)}
                                                        disabled={busy}
                                                        aria-label="Filter unused sections"
                                                    />
                                                </div>
                                                {visibleUnusedCatalog.length === 0 ? (
                                                    <p className="text-muted-foreground text-sm">
                                                        No unused sections match the current filter.
                                                    </p>
                                                ) : (
                                                    <div className="space-y-3">
                                                        {unusedSectionsBySource.map(([sourceCode, rows]) => {
                                                            const selectedInSource = rows.filter(r => selected.has(r.section_guid)).length;
                                                            return (
                                                                <Collapsible
                                                                    key={sourceCode}
                                                                    defaultOpen={false}
                                                                >
                                                                    <div className="flex w-full items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
                                                                        <CollapsibleTrigger className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left">
                                                                            <span className="truncate font-mono text-xs font-medium">
                                                                                {sourceCode}
                                                                            </span>
                                                                            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                                                                        </CollapsibleTrigger>
                                                                        <span className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs">
                                                                            {selectedInSource}/{rows.length} selected
                                                                            <Button
                                                                                type="button"
                                                                                variant="outline"
                                                                                size="sm"
                                                                                className="h-7 px-2"
                                                                                disabled={busy}
                                                                                onClick={() => selectUnusedBySource(sourceCode)}
                                                                            >
                                                                                Select all
                                                                            </Button>
                                                                        </span>
                                                                    </div>
                                                                    <CollapsibleContent className="pt-2">
                                                                        <div className="rounded-md border bg-background">
                                                                            <Table>
                                                                                <TableHeader>
                                                                                    <TableRow>
                                                                                        <TableHead className="w-12" />
                                                                                        <TableHead className="w-28">Source</TableHead>
                                                                                        <TableHead className="w-14 text-right">
                                                                                            Orig Ch.
                                                                                        </TableHead>
                                                                                        <TableHead className="w-14 text-right">
                                                                                            Orig Sec.
                                                                                        </TableHead>
                                                                                        <TableHead>Title</TableHead>
                                                                                        <TableHead className="w-14">GUID</TableHead>
                                                                                        <TableHead className="w-30 text-center">
                                                                                            MA doc
                                                                                        </TableHead>
                                                                                        <TableHead className="hidden md:table-cell">
                                                                                            gdoc
                                                                                        </TableHead>
                                                                                        <TableHead className="w-36">Status</TableHead>
                                                                                    </TableRow>
                                                                                </TableHeader>
                                                                                <TableBody>
                                                                                    {rows.map(row => {
                                                                                        const st = rowStates[row.section_guid] ?? {
                                                                                            status: 'idle',
                                                                                            error: null,
                                                                                        };
                                                                                        const maDocumentName =
                                                                                            getRowMaDocumentName(
                                                                                                row,
                                                                                                documentNamesBySectionKey,
                                                                                                documentNamesByGuid,
                                                                                            )?.trim() || null;
                                                                                        const maDocumentId =
                                                                                            getRowMaDocumentId(
                                                                                                row,
                                                                                                documentIdsBySectionKey,
                                                                                                documentIdsByGuid,
                                                                                            ) ?? null;
                                                                                        return (
                                                                                            <ImportCatalogTableRow
                                                                                                key={row.section_guid}
                                                                                                row={row}
                                                                                                isSelected={selected.has(row.section_guid)}
                                                                                                maDocumentName={maDocumentName}
                                                                                                maDocumentId={maDocumentId}
                                                                                                rowState={st}
                                                                                                checkboxDisabled={
                                                                                                    busy ||
                                                                                                    st.status === 'pending' ||
                                                                                                    st.status === 'importing'
                                                                                                }
                                                                                                onToggleGuid={toggleGuid}
                                                                                                variant="unused"
                                                                                                documentZybookCode={
                                                                                                    activeZybookCode ?? undefined
                                                                                                }
                                                                                            />
                                                                                        );
                                                                                    })}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </div>
                                                                    </CollapsibleContent>
                                                                </Collapsible>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </CollapsibleContent>
                                        </div>
                                    </Collapsible>
                                </section>
                            )}

                            {lastLoadedWithAboutPagesEnabled === true && aboutPageCatalog.length > 0 && (
                                <section>
                                    <Collapsible
                                        open={aboutPanelOpen}
                                        onOpenChange={setAboutPanelOpen}
                                    >
                                        <div className="overflow-hidden rounded-lg border-2 border-violet-500/45 bg-card shadow-md ring-1 ring-violet-500/15">
                                            <CollapsibleTrigger className="flex w-full cursor-pointer items-start justify-between gap-3 border-b border-violet-500/20 bg-violet-500/5 px-4 py-3.5 text-left transition-colors hover:bg-violet-500/10">
                                                <div className="space-y-1">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <h2 className="text-base font-semibold">About pages</h2>
                                                        <Badge
                                                            variant="outline"
                                                            className="border-violet-500/40 bg-violet-500/10 text-violet-900 dark:text-violet-200"
                                                        >
                                                            {aboutPageCatalog.length}
                                                        </Badge>
                                                        {selectedAboutCount > 0 && (
                                                            <Badge variant="default">{selectedAboutCount} selected</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-muted-foreground text-xs">
                                                        Subject-linked about page sections from /about at chapter 0 (not in catalog TOC).
                                                        Import requires a gdoc.
                                                    </p>
                                                </div>
                                                {aboutPanelOpen ? (
                                                    <ChevronDown className="mt-1 size-4 shrink-0 text-muted-foreground" />
                                                ) : (
                                                    <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
                                                )}
                                            </CollapsibleTrigger>
                                            <CollapsibleContent className="space-y-3 bg-background px-4 pb-4 pt-3">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={selectAllAbout}
                                                        disabled={busy || visibleAboutCatalog.length === 0}
                                                    >
                                                        Select all about
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={clearAbout}
                                                        disabled={busy || selectedAboutCount === 0}
                                                    >
                                                        Clear about
                                                    </Button>
                                                    <input
                                                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 min-w-[200px] flex-1 rounded-md border px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                        placeholder="Filter by title, GUID, or section id…"
                                                        value={aboutFilter}
                                                        onChange={e => setAboutFilter(e.target.value)}
                                                        disabled={busy}
                                                        aria-label="Filter about pages"
                                                    />
                                                </div>
                                                {visibleAboutCatalog.length === 0 ? (
                                                    <p className="text-muted-foreground text-sm">
                                                        No about pages match the current filter.
                                                    </p>
                                                ) : (
                                                    <div className="rounded-md border bg-background">
                                                        <Table>
                                                            <TableHeader>
                                                                <TableRow>
                                                                    <TableHead className="w-12" />
                                                                    <TableHead className="w-14 text-right">Ch.</TableHead>
                                                                    <TableHead className="w-14 text-right">Sec.</TableHead>
                                                                    <TableHead>Title</TableHead>
                                                                    <TableHead className="w-14">GUID</TableHead>
                                                                    <TableHead className="w-30 text-center">MA doc</TableHead>
                                                                    <TableHead className="hidden md:table-cell">gdoc</TableHead>
                                                                    <TableHead className="w-36">Status</TableHead>
                                                                </TableRow>
                                                            </TableHeader>
                                                            <TableBody>
                                                                {visibleAboutCatalog.map(row => {
                                                                    const st = rowStates[row.section_guid] ?? {
                                                                        status: 'idle',
                                                                        error: null,
                                                                    };
                                                                    const maDocumentName =
                                                                        getRowMaDocumentName(
                                                                            row,
                                                                            documentNamesBySectionKey,
                                                                            documentNamesByGuid,
                                                                        )?.trim() || null;
                                                                    const maDocumentId =
                                                                        getRowMaDocumentId(
                                                                            row,
                                                                            documentIdsBySectionKey,
                                                                            documentIdsByGuid,
                                                                        ) ?? null;
                                                                    return (
                                                                        <ImportCatalogTableRow
                                                                            key={row.section_guid}
                                                                            row={row}
                                                                            isSelected={selected.has(row.section_guid)}
                                                                            maDocumentName={maDocumentName}
                                                                            maDocumentId={maDocumentId}
                                                                            rowState={st}
                                                                            checkboxDisabled={
                                                                                busy || st.status === 'pending' || st.status === 'importing'
                                                                            }
                                                                            onToggleGuid={toggleGuid}
                                                                            documentZybookCode={activeZybookCode ?? undefined}
                                                                        />
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </div>
                                                )}
                                            </CollapsibleContent>
                                        </div>
                                    </Collapsible>
                                </section>
                            )}

                            {lastLoadedWithTestQuestionsEnabled === true && (
                                <section>
                                    <Collapsible
                                        open={testQuestionsPanelOpen}
                                        onOpenChange={setTestQuestionsPanelOpen}
                                    >
                                        <div className="overflow-hidden rounded-lg border-2 border-amber-500/45 bg-card shadow-md ring-1 ring-amber-500/15">
                                            <CollapsibleTrigger className="flex w-full cursor-pointer items-start justify-between gap-3 border-b border-amber-500/20 bg-amber-500/5 px-4 py-3.5 text-left transition-colors hover:bg-amber-500/10">
                                                <div className="space-y-1">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <h2 className="text-base font-semibold">Test questions</h2>
                                                        <Badge
                                                            variant="outline"
                                                            className="border-amber-600/30 bg-amber-500/10 text-amber-950 dark:text-amber-100"
                                                        >
                                                            {visibleTestQuestionSectionCount}
                                                        </Badge>
                                                        {selectedTestQuestionSectionCount > 0 && (
                                                            <Badge variant="default">{selectedTestQuestionSectionCount} selected</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-muted-foreground text-xs">
                                                        Test bank questions from same-content-source subjects (latest release). Import by
                                                        section only; duplicate GUIDs are collapsed to the highest release id.
                                                    </p>
                                                </div>
                                                {testQuestionsPanelOpen ? (
                                                    <ChevronDown className="mt-1 size-4 shrink-0 text-muted-foreground" />
                                                ) : (
                                                    <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
                                                )}
                                            </CollapsibleTrigger>
                                            <CollapsibleContent className="space-y-3 bg-background px-4 pb-4 pt-3">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={selectAllTestQuestionSections}
                                                        disabled={busy || visibleTestQuestionSectionCount === 0}
                                                    >
                                                        Select all sections
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={clearTestQuestionSections}
                                                        disabled={busy || selectedTestQuestionSectionCount === 0}
                                                    >
                                                        Clear sections
                                                    </Button>
                                                    <input
                                                        className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 min-w-[200px] flex-1 rounded-md border px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                        placeholder="Filter by section id, guid, question id, release id, chapter, or section title…"
                                                        value={testQuestionFilter}
                                                        onChange={e => setTestQuestionFilter(e.target.value)}
                                                        disabled={busy}
                                                        aria-label="Filter test questions"
                                                    />
                                                </div>
                                                {testQuestionCatalog.length === 0 ? (
                                                    <p className="text-muted-foreground text-sm">No test questions found.</p>
                                                ) : visibleTestQuestionCatalog.length === 0 ? (
                                                    <p className="text-muted-foreground text-sm">
                                                        No test questions match the current filter.
                                                    </p>
                                                ) : (
                                                    <div className="space-y-3">
                                                        {testQuestionsBySection.map(group => {
                                                            const isSelected = selectedTestQuestionSectionKeys.has(group.sectionKey);
                                                            const sectionImportState = getTestQuestionSectionImportState(
                                                                group.sectionKey,
                                                                testQuestionImportStates,
                                                            );
                                                            const sectionPlacementMissing =
                                                                group.matched_section_title == null || !group.matched_section_guid;
                                                            return (
                                                                <Collapsible
                                                                    key={group.sectionKey}
                                                                    defaultOpen={false}
                                                                >
                                                                    <div className="flex w-full items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
                                                                        <div
                                                                            className="flex min-w-0 flex-1 items-center gap-3"
                                                                            onClick={e => e.stopPropagation()}
                                                                        >
                                                                            <Checkbox
                                                                                checked={isSelected}
                                                                                disabled={busy || sectionPlacementMissing}
                                                                                onCheckedChange={() =>
                                                                                    toggleTestQuestionSection(group.sectionKey)
                                                                                }
                                                                                aria-label={`Select test questions for chapter ${group.matched_chapter_number} section ${group.matched_section_number}`}
                                                                            />
                                                                            <CollapsibleTrigger className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left">
                                                                                <div className="min-w-0">
                                                                                    <div className="truncate text-sm font-medium">
                                                                                        {group.matched_section_title ?? 'Unplaced section'}
                                                                                    </div>
                                                                                    <div className="text-muted-foreground truncate text-xs">
                                                                                        Ch {group.matched_chapter_number} Sec{' '}
                                                                                        {group.matched_section_number}
                                                                                        {` · ${group.rows.length} question${group.rows.length === 1 ? '' : 's'}`}
                                                                                    </div>
                                                                                </div>
                                                                                <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                                                                            </CollapsibleTrigger>
                                                                        </div>
                                                                        <StatusCell
                                                                            state={
                                                                                sectionImportState ?? {
                                                                                    status: 'idle',
                                                                                    error: null,
                                                                                }
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <CollapsibleContent className="pt-2">
                                                                        <div className="rounded-md border bg-background">
                                                                            <Table>
                                                                                <TableHeader>
                                                                                    <TableRow>
                                                                                        <TableHead className="w-12 text-right">
                                                                                            Q#
                                                                                        </TableHead>
                                                                                        <TableHead className="w-24 text-right">
                                                                                            ID
                                                                                        </TableHead>
                                                                                        <TableHead className="w-20 text-right">
                                                                                            Subject id
                                                                                        </TableHead>
                                                                                        <TableHead className="w-20 text-right">
                                                                                            Release id
                                                                                        </TableHead>
                                                                                        <TableHead>GUID</TableHead>
                                                                                        <TableHead className="w-28">Status</TableHead>
                                                                                    </TableRow>
                                                                                </TableHeader>
                                                                                <TableBody>
                                                                                    {group.rows.map(row => (
                                                                                        <TestQuestionCatalogTableRow
                                                                                            key={row.rowKey}
                                                                                            row={row}
                                                                                            importState={sectionImportState}
                                                                                        />
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </div>
                                                                    </CollapsibleContent>
                                                                </Collapsible>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {visibleTestQuestionSectionCount > 0 && (
                                                    <p className="text-muted-foreground text-xs">
                                                        {selectedVisibleTestQuestionSectionCount} of {visibleTestQuestionSectionCount}{' '}
                                                        visible sections selected
                                                    </p>
                                                )}
                                            </CollapsibleContent>
                                        </div>
                                    </Collapsible>
                                </section>
                            )}
                        </div>
                    )}
                </div>
            </main>

            {importLogsDialogOpen && (
                <ImportLogsDialog
                    onOpenChange={setImportLogsDialogOpen}
                    defaultZybookCode={activeZybookCode ?? zybookCodeInput.trim()}
                />
            )}

            <AlertDialog
                open={importDialogOpen}
                onOpenChange={setImportDialogOpen}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Import selected content?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div>
                                {selectedCount > 0 ? (
                                    <>
                                        This will import {selectedCount} selected section
                                        {selectedCount === 1 ? '' : 's'}
                                        {activeZybookCode ? ` for ${activeZybookCode}` : ''}. Existing MA documents for those chapter and
                                        section pairs will be overwritten.
                                    </>
                                ) : null}
                                {selectedCount > 0 && selectedTestQuestionGroupCount > 0 ? ' ' : null}
                                {selectedTestQuestionGroupCount > 0 ? (
                                    <>
                                        <div>
                                            {selectedCount > 0 ? 'It will also import ' : 'This will import '}
                                            {selectedTestQuestionGroupCount} test question group
                                            {selectedTestQuestionGroupCount === 1 ? '' : 's'}
                                            {activeZybookCode ? ` for ${activeZybookCode}` : ''}, merged into one MA document per import
                                            section.
                                        </div>

                                        <div>
                                            *When multiple questions share the same GUID, only the one with the higher release id is
                                            imported.
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            type="button"
                            onClick={() => performImport()}
                        >
                            Import
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete MA documents?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes {selectedWithMaDocCount} selected row
                            {selectedWithMaDocCount === 1 ? '' : 's'} that have an MA document from the database and deletes the matching
                            TipTap Cloud documents. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            type="button"
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => performDeleteSelected()}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
